import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Html5Qrcode } from "html5-qrcode";
import { 
  RefreshCw, Search, ScanBarcode, Clock, AlertTriangle, 
  Play, Pause, CheckCircle, ArrowRight, LogOut, User,
  Factory, Printer, Scissors, Package, Truck, Palette, Image, FileText,
  X, Camera, Keyboard, Hash, MapPin, Eye, Plus, Trash2, Zap, Shield, Send, Download,
  Calendar, ChevronLeft, ChevronRight, Clipboard, Check, Maximize2, Minimize2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Extracted components
import OperatorWorkTerminal from "@/components/work-terminal/OperatorWorkTerminal";
import DeliveryWhiteboard from "@/components/delivery/DeliveryWhiteboard";
import DeliveryRequestForm from "@/components/delivery/DeliveryRequestForm";
import SOPToolkit from "@/components/sop/SOPToolkit";
import ToolsHub from "@/components/hub/ToolsHub";
import DispatchBoard from "@/components/dispatch/DispatchBoard";
import BoxLabelsTool from "@/components/sop/BoxLabelsTool";
import AdminPanelModal from "@/components/admin/AdminPanelModal";
import KioskProductionBoard from "@/components/kiosk/KioskProductionBoard";
import KioskDeliveryBoard from "@/components/kiosk/KioskDeliveryBoard";
import KioskWideFormatDept from "@/components/kiosk/KioskWideFormatDept";
import KioskPrepressDept from "@/components/kiosk/KioskPrepressDept";
import WideFormatDashboard from "@/components/wide-format/WideFormatDashboard";
import SalesDashboard from "@/components/sales/SalesDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

// Sound notification utility using Web Audio API
const SoundNotification = {
  audioContext: null,
  
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  },
  
  playTone(frequency, duration, type = 'sine', volume = 0.3) {
    try {
      const ctx = this.init();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      
      gainNode.gain.setValueAtTime(volume, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.log('Sound not available:', e);
    }
  },
  
  // Pleasant success chime (two ascending tones)
  success() {
    this.playTone(523.25, 0.15, 'sine', 0.2); // C5
    setTimeout(() => this.playTone(659.25, 0.2, 'sine', 0.2), 100); // E5
    setTimeout(() => this.playTone(783.99, 0.3, 'sine', 0.15), 200); // G5
  },
  
  // Subtle error sound (descending tone)
  error() {
    this.playTone(440, 0.15, 'square', 0.15); // A4
    setTimeout(() => this.playTone(349.23, 0.25, 'square', 0.1), 120); // F4
  },
  
  // Quick click for actions
  click() {
    this.playTone(800, 0.05, 'sine', 0.1);
  }
};

// Default fallback configuration (used while loading from API)
const DEFAULT_CONFIG = {
  statusMap: {},
  boardStages: ['Artwork', 'Prepress', 'Digital Print', 'Litho Print', 'Wide Format', 'Finishing', 'Packaging', 'Dispatch'],
  routeTemplates: {
    standard: ['Artwork', 'Prepress', 'Finishing', 'Packaging', 'Dispatch'],
    digital: ['Artwork', 'Prepress', 'Digital Print', 'Finishing', 'Packaging', 'Dispatch'],
    litho: ['Artwork', 'Prepress', 'Litho Print', 'Finishing', 'Packaging', 'Dispatch'],
    'wide-format': ['Artwork', 'Prepress', 'Wide Format', 'Finishing', 'Packaging', 'Dispatch'],
  },
  boardStatuses: ['Queued', 'Ready', 'In Production', 'Paused', 'Blocked', 'Complete', 'Dispatched', 'Part Delivered'],
};

// Stage icons mapping
const stageIcons = {
  'Artwork': Palette,
  'Prepress': FileText,
  'Digital Print': Printer,
  'Litho Print': Factory,
  'Wide Format': Image,
  'Finishing': Scissors,
  'Packaging': Package,
  'Dispatch': Truck,
};

// Sample PrintLogic jobs (would come from PrintLogic API in production)
const printLogicJobs = [
  {
    id: '14530',
    customer: 'XYZ Industries',
    product: 'Brochure 32pp',
    qty: 5000,
    due: 'Today 15:00',
    printLogicStatus: 'Problem with PDF | Job Info',
    operator: null,
    machine: null,
    elapsed: null,
  },
  {
    id: '14522',
    customer: 'ABC Retail',
    product: 'Flyers',
    qty: 15000,
    due: 'Today 14:00',
    printLogicStatus: 'Prepress_Digital',
    operator: 'Sarah',
    machine: 'Prepress Station 2',
    elapsed: '00:09',
  },
  {
    id: '14518',
    customer: 'DEF Ltd',
    product: 'Posters A2',
    qty: 250,
    due: 'Today 12:30',
    printLogicStatus: 'Digital Print',
    boardStatusOverride: 'In Production',
    operator: 'John',
    machine: 'Indigo 7900',
    elapsed: '00:18',
  },
  {
    id: '14512',
    customer: 'DEF Ltd',
    product: 'Magazines 32pp',
    qty: 20000,
    due: 'Tomorrow',
    printLogicStatus: 'Litho Print',
    operator: 'Mike',
    machine: 'Heidelberg XL',
    elapsed: null,
  },
  {
    id: '14533',
    customer: 'Venue Group',
    product: 'Event Panels',
    qty: 12,
    due: 'Tomorrow 09:00',
    printLogicStatus: 'Wide Format',
    operator: 'Ava',
    machine: 'HP Latex 800',
    elapsed: null,
  },
  {
    id: '14503',
    customer: 'Brochures Ltd',
    product: 'Folded Leaflets',
    qty: 5000,
    due: 'Today',
    printLogicStatus: 'Finishing',
    detail: 'Folding queued after print',
    operator: 'Mike',
    machine: 'Stahlfolder KH',
    elapsed: null,
  },
  {
    id: '14510',
    customer: 'Smith & Co',
    product: 'Manuals 100pg',
    qty: 2000,
    due: 'Today',
    printLogicStatus: 'Packaging',
    operator: 'Lily',
    machine: 'Packing Bench 1',
    elapsed: null,
  },
  {
    id: '14498',
    customer: 'Trust Print',
    product: 'Books 24pg',
    qty: 1200,
    due: 'Today',
    printLogicStatus: 'Finished & Boxed',
    operator: 'Lily',
    machine: 'Packing Bench 2',
    elapsed: null,
  },
  {
    id: '14490',
    customer: 'Logistics Pro',
    product: 'Catalogues',
    qty: 2200,
    due: 'Today',
    printLogicStatus: 'Dispatched',
    operator: 'Dan',
    machine: 'Van 2',
    elapsed: null,
  },
  {
    id: '14540',
    customer: 'Retail Hub',
    product: 'Window Graphics',
    qty: 40,
    due: 'Tomorrow',
    printLogicStatus: 'Digital & Wide Format',
    detail: 'Hybrid route via digital + wide format',
    operator: null,
    machine: null,
    elapsed: null,
  },
];

// Helper functions that use config
function getRouteForType(routeType, config) {
  const templates = config?.routeTemplates || DEFAULT_CONFIG.routeTemplates;
  return templates[routeType] || templates.standard;
}

function mapPrintLogicJobToBoardJob(job, config) {
  const statusMap = config?.statusMap || {};
  const mapping = statusMap[job.printLogicStatus] || {
    boardStage: 'Artwork',
    boardStatus: 'Queued',
    routeType: 'standard',
    visibleOnBoard: true,
  };

  const route = getRouteForType(mapping.routeType, config);
  const currentIndex = route.indexOf(mapping.boardStage);
  const nextStage = currentIndex < route.length - 1 ? route[currentIndex + 1] : null;

  return {
    ...job,
    stage: mapping.boardStage,
    status: job.boardStatusOverride || mapping.boardStatus,
    routeType: mapping.routeType,
    visibleOnBoard: mapping.visibleOnBoard,
    route: route,
    detail: job.detail || `${job.operator ? `Operator: ${job.operator}` : 'Awaiting operator'}${job.machine ? ` · Machine: ${job.machine}` : ''}`,
    next: nextStage ? `Move to ${nextStage}` : 'View details',
    elapsedSeconds: job.elapsed ? parseElapsedToSeconds(job.elapsed) : 0,
  };
}

function parseElapsedToSeconds(elapsed) {
  if (!elapsed) return 0;
  const parts = elapsed.split(':');
  return parseInt(parts[0]) * 60 + parseInt(parts[1]);
}

function buildBoardColumns(jobs, config) {
  const stages = config?.boardStages || DEFAULT_CONFIG.boardStages;
  const mappedJobs = jobs.map(job => mapPrintLogicJobToBoardJob(job, config)).filter((job) => job.visibleOnBoard);

  return stages.reduce((acc, stage) => {
    const stageId = stage.toLowerCase().replace(/ /g, '-');
    acc[stageId] = {
      id: stageId,
      title: stage,
      icon: stageIcons[stage] || Factory,
      // Match by lowercase stage ID, not display name
      jobs: mappedJobs.filter((job) => {
        const jobStageId = (job.stage || '').toLowerCase().replace(/ /g, '-');
        return jobStageId === stageId;
      }),
    };
    return acc;
  }, {});
}

// Build columns from live PrintLogic data (already mapped on backend)
function buildColumnsFromLiveData(jobs, config) {
  const stages = config?.boardStages || DEFAULT_CONFIG.boardStages;

  return stages.reduce((acc, stage) => {
    const stageId = stage.toLowerCase().replace(/ /g, '-');
    acc[stageId] = {
      id: stageId,
      title: stage,
      icon: stageIcons[stage] || Factory,
      jobs: jobs.filter((job) => {
        const jobStageId = (job.stage || '').toLowerCase().replace(/ /g, '-');
        return jobStageId === stageId;
      }).map(job => ({
        ...job,
        visibleOnBoard: true,
        detail: `Awaiting operator`,
        next: null,
        route: null,
      })),
    };
    return acc;
  }, {});
}

// Custom hook to fetch PrintLogic config from API
const usePrintLogicConfig = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncInfo, setSyncInfo] = useState({
    lastSyncedAt: null,
    syncedBy: null,
    source: null,
    totalStatuses: 0
  });

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/config/statuses`);
      const data = response.data;
      
      setConfig({
        statusMap: data.statusMap || {},
        boardStages: data.boardStages || DEFAULT_CONFIG.boardStages,
        routeTemplates: data.routeTemplates || DEFAULT_CONFIG.routeTemplates,
        boardStatuses: data.boardStatuses || DEFAULT_CONFIG.boardStatuses,
      });
      setSyncInfo({
        lastSyncedAt: data.lastSyncedAt,
        syncedBy: data.syncedBy,
        source: data.source,
        totalStatuses: Object.keys(data.statusMap || {}).length
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch PrintLogic config:', err);
      setError('Failed to load configuration');
      // Keep using default config on error
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncFromPrintLogic = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to sync statuses');
      return null;
    }

    try {
      setSyncing(true);
      const response = await axios.post(
        `${API}/sync/statuses`,
        { force: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { config: newConfig, syncResult } = response.data;
      
      setConfig({
        statusMap: newConfig.statusMap || {},
        boardStages: newConfig.boardStages || DEFAULT_CONFIG.boardStages,
        routeTemplates: newConfig.routeTemplates || DEFAULT_CONFIG.routeTemplates,
        boardStatuses: newConfig.boardStatuses || DEFAULT_CONFIG.boardStatuses,
      });
      
      setSyncInfo({
        lastSyncedAt: newConfig.lastSyncedAt,
        syncedBy: newConfig.syncedBy,
        source: newConfig.source,
        totalStatuses: Object.keys(newConfig.statusMap || {}).length
      });
      
      setError(null);
      
      // Show sync result
      if (syncResult.success) {
        const changes = syncResult.changes;
        if (changes.added_statuses.length > 0 || changes.removed_statuses.length > 0) {
          toast.success(
            `Sync complete! Added: ${changes.added_statuses.length}, Removed: ${changes.removed_statuses.length}`
          );
        } else {
          toast.success(`Synced ${changes.total_statuses} statuses from PrintLogic`);
        }
      } else {
        toast.warning(syncResult.message);
      }
      
      return syncResult;
    } catch (err) {
      console.error('Sync failed:', err);
      const errorMsg = err.response?.data?.detail || 'Sync failed. Using cached data.';
      setError(errorMsg);
      toast.error(errorMsg);
      return null;
    } finally {
      setSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchConfig();
    
    // Refresh config every 5 minutes
    const interval = setInterval(fetchConfig, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchConfig]);

  return { 
    config, 
    loading, 
    syncing,
    error, 
    syncInfo,
    refreshConfig: fetchConfig,
    syncFromPrintLogic
  };
};

// Auth context
const useAuth = () => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [operator, setOperator] = useState(() => {
    const saved = localStorage.getItem('operator');
    return saved ? JSON.parse(saved) : null;
  });

  const login = (tokenVal, operatorVal) => {
    localStorage.setItem('token', tokenVal);
    localStorage.setItem('operator', JSON.stringify(operatorVal));
    setToken(tokenVal);
    setOperator(operatorVal);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('operator');
    setToken(null);
    setOperator(null);
  };

  return { token, operator, login, logout, isAuthenticated: !!token };
};

// Login Page - Department-based two-step login
const LoginPage = ({ onLogin }) => {
  const [step, setStep] = useState(1); // 1 = department login, 2 = select operator
  const [department, setDepartment] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Step 2 state
  const [departmentToken, setDepartmentToken] = useState('');
  const [departmentInfo, setDepartmentInfo] = useState(null);
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [authNotice] = useState(() => {
    const n = sessionStorage.getItem('authNotice');
    sessionStorage.removeItem('authNotice');
    return n || '';
  });

  const handleDepartmentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/department-login`, {
        department,
        password
      });
      
      setDepartmentToken(response.data.department_token);
      setDepartmentInfo(response.data.department);
      setOperators(response.data.operators);
      setStep(2);
      toast.success(`Welcome to ${response.data.department.display_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid department or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOperator = async (operator) => {
    setSelectedOperator(operator);
    setLoading(true);
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/auth/select-operator`, {
        department_token: departmentToken,
        operator_id: operator.id
      });
      
      const authToken = response.data.access_token;
      
      // Jobs are kept fresh by the backend auto-sync (every 10 min); no blocking sync on login
      onLogin(authToken, response.data.operator);
      toast.success(`Logged in as ${operator.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select operator');
      // If token expired, go back to step 1
      if (error.response?.status === 401) {
        setStep(1);
        setDepartmentToken('');
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setDepartmentToken('');
    setDepartmentInfo(null);
    setOperators([]);
    setSelectedOperator(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden noise-bg" data-testid="login-page">
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600951105641-d2e6840da022?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      
      <Card className="relative z-10 w-full max-w-md p-8 bg-zinc-900/80 backdrop-blur-xl border-zinc-800 rounded-sm" data-testid="login-card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <Factory className="w-8 h-8 text-cyan-500" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight uppercase text-zinc-100">
            PrintLogic
          </h1>
          <p className="text-zinc-500 mt-2 text-sm uppercase tracking-widest">
            Shop Floor Control
          </p>
        </div>

        {step === 1 ? (
          /* Step 1: Department Login */
          <form onSubmit={handleDepartmentLogin} className="space-y-4">
            {authNotice && (
              <div className="rounded-sm border border-amber-500/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-300" data-testid="login-auth-notice">
                {authNotice}
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Department
              </label>
              <Input
                data-testid="login-department"
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-zinc-950 border-zinc-800 rounded-sm h-12 text-lg"
                placeholder="e.g., Litho, Digital, Finishing"
                required
                autoFocus
              />
            </div>
            
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Password
              </label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-950 border-zinc-800 rounded-sm h-12 text-lg"
                placeholder="Enter department password"
                required
              />
            </div>

            <Button 
              data-testid="login-submit"
              type="submit" 
              className="w-full h-14 bg-cyan-600 hover:bg-cyan-500 text-white font-bold uppercase tracking-wide rounded-sm text-lg touch-target"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Continue'}
            </Button>
            
            <p className="text-center text-zinc-600 text-xs mt-4">
              Enter your department name and shared password
            </p>
          </form>
        ) : (
          /* Step 2: Select Operator */
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleBack}
                disabled={loading || syncing}
                className="text-zinc-500 hover:text-cyan-500 transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </button>
              <div className="text-right">
                <p className="text-cyan-500 font-bold uppercase tracking-wide">
                  {departmentInfo?.display_name}
                </p>
              </div>
            </div>
            
            {syncing ? (
              <div className="text-center py-12">
                <RefreshCw className="w-12 h-12 text-cyan-500 mx-auto mb-4 animate-spin" />
                <p className="text-white text-xl font-bold">Syncing Jobs</p>
                <p className="text-zinc-400 mt-2">Fetching latest jobs from PrintLogic...</p>
              </div>
            ) : (
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3 block text-center">
                  Select Your Name
                </label>
                
                <div className="grid gap-2">
                  {operators.map((operator) => (
                    <button
                      key={operator.id}
                      onClick={() => handleSelectOperator(operator)}
                      disabled={loading && selectedOperator?.id === operator.id}
                      className={`
                        w-full p-4 rounded-sm border-2 transition-all text-left
                        ${selectedOperator?.id === operator.id
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-zinc-800 bg-zinc-950 hover:border-cyan-500/50 hover:bg-zinc-900'
                        }
                      `}
                      data-testid={`operator-${operator.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-cyan-500" />
                          </div>
                          <span className="text-lg font-bold text-zinc-100">
                            {operator.name}
                          </span>
                        </div>
                        {loading && selectedOperator?.id === operator.id && (
                          <RefreshCw className="w-5 h-5 text-cyan-500 animate-spin" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                
                {operators.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">
                    No operators found in this department
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

// Barcode Scanner Modal Component
const BarcodeScannerModal = ({ isOpen, onClose, onJobFound, allJobs }) => {
  const [mode, setMode] = useState('manual'); // 'manual' or 'camera'
  const [manualInput, setManualInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);

  // Cleanup scanner on unmount or mode change
  useEffect(() => {
    return () => {
      if (html5QrcodeRef.current) {
        html5QrcodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Stop scanner when modal closes
  useEffect(() => {
    if (!isOpen && html5QrcodeRef.current) {
      html5QrcodeRef.current.stop().catch(() => {});
      setIsScanning(false);
    }
  }, [isOpen]);

  const startCamera = async () => {
    setCameraError(null);
    setIsScanning(true);
    
    try {
      if (!html5QrcodeRef.current) {
        html5QrcodeRef.current = new Html5Qrcode("barcode-scanner");
      }
      
      await html5QrcodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 100 },
          aspectRatio: 1.777,
        },
        (decodedText) => {
          // Barcode scanned successfully
          handleBarcodeScanned(decodedText);
        },
        (errorMessage) => {
          // Scan error (ignore - continuous scanning)
        }
      );
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError("Unable to access camera. Please check permissions or use manual entry.");
      setIsScanning(false);
    }
  };

  const stopCamera = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
      } catch (err) {
        console.error("Error stopping camera:", err);
      }
    }
    setIsScanning(false);
  };

  const handleBarcodeScanned = (barcode) => {
    // Extract job number from barcode (remove any prefix/suffix)
    const jobNumber = barcode.replace(/\D/g, '');
    if (jobNumber) {
      stopCamera();
      searchJob(jobNumber);
    }
  };

  const searchJob = (query) => {
    const searchTerm = query.toLowerCase().trim();
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    const results = allJobs.filter(job => 
      job.id.toLowerCase().includes(searchTerm) ||
      job.customer.toLowerCase().includes(searchTerm) ||
      job.product.toLowerCase().includes(searchTerm)
    );
    
    setSearchResults(results);
    
    if (results.length === 1) {
      // Exact match - auto-select
      toast.success(`Found Job #${results[0].id}`);
    } else if (results.length === 0) {
      toast.error(`No jobs found matching "${query}"`);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    searchJob(manualInput);
  };

  const handleSelectJob = (job) => {
    onJobFound(job);
    onClose();
    setManualInput('');
    setSearchResults([]);
    toast.success(`Navigating to Job #${job.id}`);
  };

  const handleModeSwitch = (newMode) => {
    if (mode === 'camera' && isScanning) {
      stopCamera();
    }
    setMode(newMode);
    setSearchResults([]);
    setCameraError(null);
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      'Queued': 'bg-zinc-700 text-zinc-300',
      'Ready': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'In Production': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'Paused': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Blocked': 'bg-red-500/20 text-red-400 border-red-500/30',
      'Complete': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Dispatched': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Part Delivered': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    };
    return styles[status] || 'bg-zinc-700 text-zinc-300';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg p-0 overflow-hidden" data-testid="barcode-scanner-modal">
        <DialogHeader className="p-4 border-b border-zinc-800 bg-zinc-950">
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide flex items-center gap-3">
            <ScanBarcode className="w-6 h-6 text-cyan-500" />
            Job Lookup
          </DialogTitle>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            data-testid="manual-search-tab"
            onClick={() => handleModeSwitch('manual')}
            className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-colors ${
              mode === 'manual' 
                ? 'bg-cyan-500/10 text-cyan-400 border-b-2 border-cyan-500' 
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            <Keyboard className="w-4 h-4" />
            Manual Search
          </button>
          <button
            data-testid="camera-scan-tab"
            onClick={() => handleModeSwitch('camera')}
            className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-colors ${
              mode === 'camera' 
                ? 'bg-cyan-500/10 text-cyan-400 border-b-2 border-cyan-500' 
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            <Camera className="w-4 h-4" />
            Scan Barcode
          </button>
        </div>

        <div className="p-4">
          {/* Manual Search Mode */}
          {mode === 'manual' && (
            <div className="space-y-4">
              <form onSubmit={handleManualSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                  <Input
                    data-testid="manual-job-input"
                    type="text"
                    placeholder="Enter job number, customer, or product..."
                    value={manualInput}
                    onChange={(e) => {
                      setManualInput(e.target.value);
                      if (e.target.value.length >= 2) {
                        searchJob(e.target.value);
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    className="pl-10 bg-zinc-950 border-zinc-800 rounded-sm h-12 text-lg font-mono"
                    autoFocus
                  />
                </div>
                <Button 
                  type="submit"
                  data-testid="search-job-btn"
                  className="h-12 px-6 bg-cyan-600 hover:bg-cyan-500 rounded-sm font-bold uppercase"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </form>
              
              <p className="text-xs text-zinc-600 uppercase tracking-wide">
                Search by job number, customer name, or product
              </p>
            </div>
          )}

          {/* Camera Scan Mode */}
          {mode === 'camera' && (
            <div className="space-y-4">
              <div 
                id="barcode-scanner" 
                ref={scannerRef}
                className="w-full h-48 bg-zinc-950 rounded-sm overflow-hidden border border-zinc-800 flex items-center justify-center"
              >
                {!isScanning && !cameraError && (
                  <div className="text-center">
                    <Camera className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-600 text-sm">Camera preview will appear here</p>
                  </div>
                )}
                {cameraError && (
                  <div className="text-center p-4">
                    <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                    <p className="text-amber-400 text-sm">{cameraError}</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                {!isScanning ? (
                  <Button 
                    data-testid="start-camera-btn"
                    onClick={startCamera}
                    className="flex-1 h-12 bg-cyan-600 hover:bg-cyan-500 rounded-sm font-bold uppercase"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Start Camera
                  </Button>
                ) : (
                  <Button 
                    data-testid="stop-camera-btn"
                    onClick={stopCamera}
                    variant="outline"
                    className="flex-1 h-12 border-zinc-700 hover:bg-zinc-800 rounded-sm font-bold uppercase"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Stop Scanning
                  </Button>
                )}
              </div>

              <p className="text-xs text-zinc-600 uppercase tracking-wide text-center">
                Point camera at job barcode to scan
              </p>
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="search-results">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                {searchResults.length} {searchResults.length === 1 ? 'Result' : 'Results'} Found
              </p>
              <div className="max-h-64 overflow-y-auto space-y-2 scrollbar-thin">
                {searchResults.map(job => (
                  <button
                    key={job.id}
                    data-testid={`search-result-${job.id}`}
                    onClick={() => handleSelectJob(job)}
                    className="w-full text-left bg-zinc-950 border border-zinc-800 rounded-sm p-3 hover:border-cyan-500/50 hover:bg-zinc-900 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-lg font-bold text-cyan-400">#{job.id}</span>
                          <Badge className={`${getStatusBadgeStyle(job.status)} border text-xs`}>
                            {job.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">{job.customer}</p>
                        <p className="text-sm text-zinc-500">{job.product} · {job.qty.toLocaleString()} units</p>
                      </div>
                      <div className="flex items-center gap-1 text-zinc-600 group-hover:text-cyan-400 transition-colors">
                        <MapPin className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase">{job.stage}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick Tips */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800">
          <p className="text-xs text-zinc-600 text-center">
            <span className="font-bold text-zinc-500">TIP:</span> Scan barcode or type job # to quickly find and navigate to any job
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Manual Ticket Modal Component
const ManualTicketModal = ({ isOpen, onClose, onCreateTicket, targetColumn }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contact, setContact] = useState('');
  const [location, setLocation] = useState('');
  const [dueDate, setDueDate] = useState('Today');
  const [priority, setPriority] = useState('Normal');

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setContact('');
    setLocation('');
    setDueDate('Today');
    setPriority('Normal');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    const newTicket = {
      id: `MT-${Date.now().toString().slice(-6)}`,
      title: title.trim(),
      description: description.trim(),
      contact: contact.trim(),
      location: location.trim(),
      due: dueDate,
      priority,
      status: 'Queued',
      isManualTicket: true,
      createdAt: new Date().toISOString(),
    };

    onCreateTicket(newTicket, targetColumn);
    resetForm();
    onClose();
    toast.success(`Manual ticket created: ${newTicket.id}`);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md p-0 overflow-hidden" data-testid="manual-ticket-modal">
        <DialogHeader className="p-4 border-b border-zinc-800 bg-zinc-950">
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide flex items-center gap-3">
            <FileText className="w-6 h-6 text-amber-500" />
            Create Manual Ticket
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Target Column Info */}
          <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-sm p-3">
            <MapPin className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-500">Adding to:</span>
            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 font-bold uppercase">
              {targetColumn?.title || 'Board'}
            </Badge>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
              Task Title *
            </label>
            <Input
              data-testid="ticket-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-zinc-950 border-zinc-800 rounded-sm h-12"
              placeholder="e.g., Collect paper from merchant"
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
              Description
            </label>
            <textarea
              data-testid="ticket-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-zinc-100 placeholder:text-zinc-600 min-h-[80px] resize-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Additional details about the task..."
            />
          </div>

          {/* Contact & Location Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Contact / Supplier
              </label>
              <Input
                data-testid="ticket-contact"
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="bg-zinc-950 border-zinc-800 rounded-sm h-10"
                placeholder="e.g., Paper Co Ltd"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Location
              </label>
              <Input
                data-testid="ticket-location"
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="bg-zinc-950 border-zinc-800 rounded-sm h-10"
                placeholder="e.g., Unit 5, Industrial Estate"
              />
            </div>
          </div>

          {/* Due Date & Priority Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Due
              </label>
              <select
                data-testid="ticket-due"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-10 px-3 text-zinc-100 focus:ring-1 focus:ring-cyan-500"
              >
                <option value="ASAP">ASAP</option>
                <option value="Today">Today</option>
                <option value="Today AM">Today AM</option>
                <option value="Today PM">Today PM</option>
                <option value="Tomorrow">Tomorrow</option>
                <option value="This Week">This Week</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Priority
              </label>
              <select
                data-testid="ticket-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-10 px-3 text-zinc-100 focus:ring-1 focus:ring-cyan-500"
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-12 border-zinc-700 hover:bg-zinc-800 rounded-sm font-bold uppercase"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="create-ticket-btn"
              className="flex-1 h-12 bg-amber-600 hover:bg-amber-500 text-white rounded-sm font-bold uppercase"
            >
              Create Ticket
            </Button>
          </div>
        </form>

        {/* Info */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800">
          <p className="text-xs text-zinc-600 text-center">
            <span className="font-bold text-zinc-500">NOTE:</span> Manual tickets are not synced with PrintLogic
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// PrintLogic API Test Modal Component (Admin Only)
const PrintLogicTestModal = ({ isOpen, onClose }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Fetch API status and available statuses when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchApiStatus();
      fetchAvailableStatuses();
      setTestResult(null);
      setShowConfirm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchApiStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/admin/printlogic/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiStatus(response.data);
    } catch (err) {
      setApiStatus({ configured: false, error: err.message });
    }
  };

  const fetchAvailableStatuses = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/admin/printlogic/available-statuses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableStatuses(response.data.statuses || []);
    } catch (err) {
      console.error('Failed to fetch statuses:', err);
    }
  };

  const handleSendTest = async () => {
    if (!orderNumber || !selectedStatus) {
      toast.error('Please enter order number and select a status');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to use test tool');
      return;
    }

    setLoading(true);
    setTestResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/printlogic/test-update-status`,
        {
          order_number: parseInt(orderNumber),
          status: selectedStatus,
          confirm: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult(response.data);
      
      if (response.data.api_result?.success) {
        toast.success('PrintLogic API call successful!');
      } else {
        toast.warning('PrintLogic API returned an error - see details below');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      setTestResult({
        api_result: {
          success: false,
          error: errorMsg,
          http_status: err.response?.status
        }
      });
      toast.error(`API call failed: ${errorMsg}`);
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleClose = () => {
    setOrderNumber('');
    setSelectedStatus('');
    setTestResult(null);
    setShowConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="printlogic-test-modal">
        <DialogHeader className="p-4 border-b border-zinc-800 bg-red-950/50">
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-500" />
            PrintLogic API Test
            <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 text-xs ml-2">
              ADMIN ONLY
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Warning Banner */}
        <div className="bg-amber-950/50 border-b border-amber-800/50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-400 uppercase text-sm">Warning: Live API Test</p>
              <p className="text-amber-300/70 text-sm mt-1">
                This tool sends REAL requests to PrintLogic. Use only with test order numbers.
                Do not use for production orders until integration is fully approved.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* API Status */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">API Connection Status</span>
              {apiStatus?.configured ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  <CheckCircle className="w-3 h-3 mr-1" /> Configured
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                  <X className="w-3 h-3 mr-1" /> Not Configured
                </Badge>
              )}
            </div>
            {apiStatus && (
              <p className="text-xs text-zinc-500 mt-2 font-mono">{apiStatus.api_url}</p>
            )}
          </div>

          {/* Input Form */}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Order Number *
              </label>
              <Input
                data-testid="test-order-number"
                type="number"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                className="bg-zinc-950 border-zinc-800 rounded-sm h-12 font-mono text-lg"
                placeholder="e.g., 10001"
                min="1"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">
                Status *
              </label>
              <select
                data-testid="test-status-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-12 px-3 text-zinc-100 focus:ring-1 focus:ring-cyan-500"
              >
                <option value="">Select a status...</option>
                {availableStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <p className="text-xs text-zinc-600 mt-1">{availableStatuses.length} statuses available</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {!showConfirm ? (
              <Button
                data-testid="prepare-test-btn"
                onClick={() => setShowConfirm(true)}
                disabled={!orderNumber || !selectedStatus || !apiStatus?.configured}
                className="w-full h-12 bg-amber-600 hover:bg-amber-500 text-white rounded-sm font-bold uppercase"
              >
                <Zap className="w-5 h-5 mr-2" />
                Prepare Test Request
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="bg-red-950/50 border border-red-800/50 rounded-sm p-3">
                  <p className="text-sm text-red-300 font-bold">Confirm: Send to PrintLogic?</p>
                  <p className="text-xs text-red-400/70 mt-1">
                    Order: <span className="font-mono">{orderNumber}</span> → Status: <span className="font-mono">{selectedStatus}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 h-12 border-zinc-700 hover:bg-zinc-800 rounded-sm font-bold uppercase"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="send-test-btn"
                    onClick={handleSendTest}
                    disabled={loading}
                    className="flex-1 h-12 bg-red-600 hover:bg-red-500 text-white rounded-sm font-bold uppercase"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" /> Send Request
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Test Result */}
          {testResult && (
            <div className="space-y-3" data-testid="test-result">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">Response</div>
              
              {/* HTTP Status */}
              <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-4 py-3">
                <span className="text-zinc-400">HTTP Status</span>
                <span className={`font-mono font-bold ${
                  testResult.api_result?.http_status === 200 ? 'text-emerald-400' :
                  testResult.api_result?.http_status ? 'text-amber-400' : 'text-zinc-500'
                }`}>
                  {testResult.api_result?.http_status || 'N/A'}
                </span>
              </div>

              {/* Result Status */}
              <div className={`rounded-sm p-4 border ${
                testResult.api_result?.success 
                  ? 'bg-emerald-950/30 border-emerald-800/50' 
                  : 'bg-red-950/30 border-red-800/50'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.api_result?.success ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="font-bold text-emerald-400">SUCCESS</span>
                    </>
                  ) : (
                    <>
                      <X className="w-5 h-5 text-red-400" />
                      <span className="font-bold text-red-400">FAILED</span>
                    </>
                  )}
                </div>
                {testResult.api_result?.error && (
                  <p className="text-sm text-red-300">{testResult.api_result.error}</p>
                )}
              </div>

              {/* Raw Response */}
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Raw API Response</div>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-xs font-mono text-zinc-400 overflow-x-auto max-h-40 overflow-y-auto">
                  {JSON.stringify(testResult.api_result?.raw_response, null, 2) || 'No response data'}
                </pre>
              </div>

              {/* Request Payload */}
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Request Sent</div>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-xs font-mono text-zinc-400 overflow-x-auto">
                  {JSON.stringify(testResult.api_result?.request_payload, null, 2) || 'No payload data'}
                </pre>
              </div>

              {/* Timestamp */}
              {testResult.api_result?.timestamp && (
                <p className="text-xs text-zinc-600 text-center">
                  Timestamp: {new Date(testResult.api_result.timestamp).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800">
          <p className="text-xs text-zinc-600 text-center">
            <span className="font-bold text-zinc-500">TEST TOOL:</span> All requests are logged for audit. Do not use for production orders.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Sync Log Viewer Modal Component (Admin)
const SyncLogModal = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'success', 'failed', 'skipped'
  const [failedCount, setFailedCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchSyncLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filter]);

  const fetchSyncLogs = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    try {
      const endpoint = filter === 'all' 
        ? `${API}/jobs/sync-log?limit=100`
        : `${API}/jobs/sync-log?status=${filter}&limit=100`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || []);

      // Also get failed count
      const failedResponse = await axios.get(`${API}/jobs/failed-syncs?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFailedCount(failedResponse.data.count || 0);
    } catch (err) {
      console.error('Failed to fetch sync logs:', err);
      toast.error('Failed to load sync logs');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryFromLog = async (logId, jobId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.post(
        `${API}/jobs/retry-sync`,
        { sync_log_id: logId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Retry successful for Job #${jobId}`);
        fetchSyncLogs(); // Refresh logs
      } else {
        toast.error(`Retry failed: ${response.data.message}`);
      }
    } catch (err) {
      toast.error(`Retry error: ${err.message}`);
    }
  };

  const getResultBadge = (result) => {
    const badges = {
      'success': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'failed': 'bg-red-500/20 text-red-400 border-red-500/30',
      'skipped': 'bg-zinc-700 text-zinc-400 border-zinc-600',
    };
    return badges[result] || 'bg-zinc-700 text-zinc-400 border-zinc-600';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl p-0 overflow-hidden max-h-[85vh]" data-testid="sync-log-modal">
        <DialogHeader className="p-4 border-b border-zinc-800 bg-zinc-950">
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide flex items-center gap-3">
            <RefreshCw className="w-6 h-6 text-cyan-500" />
            PrintLogic Sync Log
            {failedCount > 0 && (
              <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 text-xs ml-2">
                {failedCount} FAILED
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Filter Tabs */}
        <div className="flex border-b border-zinc-800">
          {[
            { value: 'all', label: 'All Syncs' },
            { value: 'success', label: 'Successful' },
            { value: 'failed', label: 'Failed' },
            { value: 'skipped', label: 'Skipped' },
          ].map(tab => (
            <button
              key={tab.value}
              data-testid={`filter-${tab.value}`}
              onClick={() => setFilter(tab.value)}
              className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wide transition-colors ${
                filter === tab.value 
                  ? 'bg-cyan-500/10 text-cyan-400 border-b-2 border-cyan-500' 
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Logs List */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No sync logs found</p>
              <p className="text-xs text-zinc-600 mt-1">Logs appear here when jobs are synced to PrintLogic</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, idx) => (
                <div 
                  key={log.log_id || idx}
                  data-testid={`log-entry-${idx}`}
                  className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-lg font-bold text-cyan-400">
                          #{log.job_id}
                        </span>
                        <Badge className={`${getResultBadge(log.sync_result)} border text-xs font-bold uppercase`}>
                          {log.sync_result}
                        </Badge>
                        {log.order_number > 0 && (
                          <span className="text-xs text-zinc-600 font-mono">
                            Order: {log.order_number}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <p className="text-zinc-500">
                          Status Change: <span className="text-zinc-300">{log.old_status} → {log.new_status}</span>
                        </p>
                        <p className="text-zinc-500">
                          Operator: <span className="text-zinc-300">{log.operator}</span>
                        </p>
                        <p className="text-zinc-500">
                          Time: <span className="text-zinc-300">{new Date(log.timestamp).toLocaleString()}</span>
                        </p>
                        {log.http_status && (
                          <p className="text-zinc-500">
                            HTTP: <span className={log.http_status === 200 ? 'text-emerald-400' : 'text-amber-400'}>{log.http_status}</span>
                          </p>
                        )}
                      </div>

                      {log.error_message && (
                        <p className="mt-2 text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded px-2 py-1">
                          {log.error_message}
                        </p>
                      )}
                    </div>

                    {log.sync_result === 'failed' && (
                      <Button
                        data-testid={`retry-log-${idx}`}
                        onClick={() => handleRetryFromLog(log.log_id, log.job_id)}
                        size="sm"
                        className="h-9 px-4 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/30 rounded-sm font-bold uppercase text-xs"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" /> Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800 flex items-center justify-between">
          <p className="text-xs text-zinc-600">
            Showing {logs.length} {filter === 'all' ? '' : filter} sync logs
          </p>
          <Button
            onClick={fetchSyncLogs}
            variant="outline"
            size="sm"
            className="border-zinc-700 hover:bg-zinc-800 rounded-sm text-xs font-bold uppercase"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Order Detail Modal Component
const OrderDetailModal = ({ isOpen, onClose, jobId }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchJobDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobId]);

  const fetchJobDetails = async () => {
    const token = localStorage.getItem('token');
    if (!token || !jobId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API}/printlogic/job/${jobId}/full-details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch job details:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const cached = data?.cached_data;
  const order = data?.printlogic_order;
  const item = data?.printlogic_item;
  const syncHistory = data?.sync_history || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl p-0 overflow-hidden max-h-[90vh]" data-testid="order-detail-modal">
        <DialogHeader className="p-4 border-b border-zinc-800 bg-zinc-950">
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide flex items-center gap-3">
            <FileText className="w-6 h-6 text-cyan-500" />
            Order #{jobId}
            {cached?.printlogic_status && (
              <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 text-xs ml-2">
                {cached.printlogic_status}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 overflow-y-auto max-h-[70vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-red-400">{error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Customer & Product Info */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Customer & Product</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-600">Customer</p>
                    <p className="text-zinc-200 font-medium">{order?.order_contact || cached?.customer || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-600">Email</p>
                    <p className="text-zinc-200">{order?.order_contact_email || cached?.customer_email || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-zinc-600">Product Description</p>
                    <p className="text-zinc-200">{order?.order_desc || cached?.product || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-600">Order Total</p>
                    <p className="text-emerald-400 font-mono font-bold">€{order?.order_total || cached?.total || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-600">PO Number</p>
                    <p className="text-zinc-200">{order?.order_po || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Dates</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-600">Received</p>
                    <p className="text-zinc-200">{order?.order_date_received || cached?.date_received || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-600">Due Date</p>
                    <p className={`font-bold ${
                      order?.order_date_due && order.order_date_due !== '0000-00-00' 
                        ? 'text-amber-400' 
                        : 'text-zinc-500'
                    }`}>
                      {order?.order_date_due && order.order_date_due !== '0000-00-00' 
                        ? order.order_date_due 
                        : 'Not set'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              {(order?.order_delivery1 || cached?.delivery_address?.line1) && (
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                  <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Delivery Address
                  </h3>
                  <div className="text-zinc-200 text-sm space-y-1">
                    {order?.order_delivery1 && <p>{order.order_delivery1}</p>}
                    {order?.order_delivery2 && <p>{order.order_delivery2}</p>}
                    {order?.order_delivery3 && <p>{order.order_delivery3}</p>}
                    {order?.order_delivery4 && <p>{order.order_delivery4}</p>}
                  </div>
                </div>
              )}

              {/* Item Details */}
              {(item || cached?.materials || cached?.printing || cached?.finishing) && (
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                  <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Item Specifications</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    {item?.finished_size_text && (
                      <div>
                        <p className="text-xs text-zinc-600">Size</p>
                        <p className="text-zinc-200">{item.finished_size_text}</p>
                      </div>
                    )}
                    {item?.pages && (
                      <div>
                        <p className="text-xs text-zinc-600">Pages</p>
                        <p className="text-zinc-200">{item.pages}</p>
                      </div>
                    )}
                    {item?.colors && (
                      <div>
                        <p className="text-xs text-zinc-600">Colors</p>
                        <p className="text-zinc-200">{item.colors}</p>
                      </div>
                    )}
                    {item?.paper_description && (
                      <div className="col-span-3">
                        <p className="text-xs text-zinc-600">Paper</p>
                        <p className="text-zinc-200">{item.paper_description}</p>
                      </div>
                    )}
                    {(item?.finishing_description || cached?.finishing) && (
                      <div className="col-span-3">
                        <p className="text-xs text-zinc-600">Finishing</p>
                        <p className="text-zinc-200">{item?.finishing_description || cached?.finishing}</p>
                      </div>
                    )}
                    {/* Wide Format Material Info */}
                    {cached?.materials && (
                      <div className="col-span-3 mt-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded">
                        <p className="text-xs text-orange-400 font-bold uppercase mb-1">Material</p>
                        <p className="text-orange-200">{cached.materials}</p>
                      </div>
                    )}
                    {cached?.printing && (
                      <div className="col-span-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                        <p className="text-xs text-blue-400 font-bold uppercase mb-1">Printing</p>
                        <p className="text-blue-200">{cached.printing}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sync History */}
              {syncHistory.length > 0 && (
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                  <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" /> Recent Sync History
                  </h3>
                  <div className="space-y-2">
                    {syncHistory.slice(0, 5).map((sync) => (
                      <div key={sync.sync_id || `${sync.job_id}-${sync.synced_at}`} className="flex items-center justify-between text-sm bg-zinc-900 px-3 py-2 rounded">
                        <div className="flex items-center gap-3">
                          <Badge className={`text-xs ${
                            sync.sync_result === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                            sync.sync_result === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-700 text-zinc-400'
                          }`}>
                            {sync.sync_result}
                          </Badge>
                          <span className="text-zinc-400">{sync.old_status} → {sync.new_status}</span>
                        </div>
                        <span className="text-zinc-600 text-xs">
                          {new Date(sync.timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Board Status */}
              {cached && (
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                  <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Board Status</h3>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs text-zinc-600">Stage</p>
                      <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                        {cached.board_stage}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-600">Status</p>
                      <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        {cached.board_status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-600">Last Synced</p>
                      <p className="text-zinc-400 text-sm">
                        {cached.last_synced_at ? new Date(cached.last_synced_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800 flex items-center justify-between">
          <p className="text-xs text-zinc-600">
            Data from PrintLogic API & local cache
          </p>
          <Button
            onClick={fetchJobDetails}
            variant="outline"
            size="sm"
            className="border-zinc-700 hover:bg-zinc-800 rounded-sm text-xs font-bold uppercase"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// NOTE ENTRY COMPONENTS FOR DELIVERY WHITEBOARD
// ============================================================

// Add to Delivery Modal - for adding jobs from Dispatch to the whiteboard
const AddToDeliveryModal = ({ isOpen, onClose, job, weekStart }) => {
  const [selectedDay, setSelectedDay] = useState('');
  const [loading, setLoading] = useState(false);
  
  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday' };

  const handleAdd = async () => {
    if (!selectedDay || !job) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/delivery-whiteboard/${weekStart}/day/${selectedDay}/jobs`,
        { job_id: job.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      SoundNotification.success();
      toast.success(`Job #${job.id} added to ${DAY_LABELS[selectedDay]}`);
      onClose();
    } catch (err) {
      if (err.response?.data?.message?.includes('already added')) {
        toast.warning('Job already added to this day');
      } else {
        toast.error('Failed to add job to delivery schedule');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !job) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wide flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-500" />
            Add to Delivery Whiteboard
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Job Info */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
            <p className="font-mono font-bold text-cyan-400">#{job.id}</p>
            <p className="text-sm text-zinc-300">{job.customer}</p>
            <p className="text-xs text-zinc-500 truncate">{job.product}</p>
          </div>

          {/* Day Selection */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Select delivery day:</label>
            <div className="grid grid-cols-5 gap-2">
              {DAYS.map(day => (
                <Button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`h-16 flex flex-col items-center justify-center rounded-sm text-xs font-bold uppercase ${
                    selectedDay === day 
                      ? 'bg-cyan-600 text-white border-2 border-cyan-400' 
                      : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700'
                  }`}
                >
                  {DAY_LABELS[day].slice(0, 3)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-zinc-700 rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selectedDay || loading}
            className="bg-cyan-600 hover:bg-cyan-500 rounded-sm"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Add to {selectedDay ? DAY_LABELS[selectedDay] : 'Day'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Move Stage Modal - for moving jobs to different stages
const MoveStageModal = ({ isOpen, onClose, job, onMoveToStage }) => {
  if (!isOpen || !job) return null;

  const stages = [
    { id: 'artwork', label: 'Artwork', icon: Palette, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { id: 'prepress', label: 'PrePress', icon: FileText, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { id: 'digital-print', label: 'Digital Print', icon: Printer, color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
    { id: 'litho-print', label: 'Litho Print', icon: Printer, color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
    { id: 'wide-format', label: 'Wide Format', icon: Image, color: 'bg-teal-500/20 text-teal-400 border-teal-500/30' },
    { id: 'finishing', label: 'Finishing', icon: Scissors, color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { id: 'finished-boxed', label: 'Finished & Boxed', icon: Package, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', isSubStatus: true },
    { id: 'packaging', label: 'Packaging', icon: Package, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { id: 'dispatch', label: 'Dispatch', icon: Truck, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-zinc-100">Move Job</h2>
              <p className="text-sm text-zinc-500 mt-1">
                #{job.id} • {job.customer}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-800 rounded-sm transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Stage Options */}
        <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Select destination</p>
          {stages.map((stage) => {
            const Icon = stage.icon;
            const isCurrentStage = job.stage === stage.id || 
              (stage.id === 'finished-boxed' && job.printlogicStatus === 'Finished & Boxed');
            
            return (
              <button
                key={stage.id}
                onClick={() => {
                  onMoveToStage(job.id, stage.id, stage.isSubStatus);
                  onClose();
                }}
                disabled={isCurrentStage}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-sm border transition-all
                  ${isCurrentStage 
                    ? 'bg-zinc-800/50 border-zinc-700 text-zinc-600 cursor-not-allowed' 
                    : `${stage.color} hover:scale-[1.02] hover:shadow-lg cursor-pointer`
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{stage.label}</span>
                {isCurrentStage && (
                  <span className="ml-auto text-xs bg-zinc-700 px-2 py-0.5 rounded">Current</span>
                )}
                {stage.isSubStatus && !isCurrentStage && (
                  <span className="ml-auto text-xs opacity-60">(Sub-status)</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-950/50">
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full border-zinc-700 hover:bg-zinc-800"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};


// Job Card Component

// ============================================
// PROJECTION DELIVERY BOARD - Light theme for 4K projector
// ============================================
const ProjectionDeliveryBoard = () => {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff)).toISOString().split('T')[0];
  });
  const [whiteboard, setWhiteboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Check if a day is in the past
  const isPastDay = (dayIndex) => {
    const [year, month, day] = weekStart.split('-').map(Number);
    const date = new Date(year, month - 1, day + dayIndex);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Calculate dynamic font size based on item count (smaller for more items)
  const getDynamicStyles = (itemCount, isPast) => {
    if (isPast) {
      // Past days use normal size with scroll
      return {
        jobText: 'text-base',
        jobTitle: 'text-lg font-bold',
        noteText: 'text-base',
        containerClass: 'overflow-y-auto max-h-[55vh]'
      };
    }
    // Current/future days: scale down text to fit without scroll
    if (itemCount <= 3) {
      return {
        jobText: 'text-base',
        jobTitle: 'text-lg font-bold',
        noteText: 'text-base',
        containerClass: 'overflow-hidden'
      };
    } else if (itemCount <= 5) {
      return {
        jobText: 'text-sm',
        jobTitle: 'text-base font-bold',
        noteText: 'text-sm',
        containerClass: 'overflow-hidden'
      };
    } else if (itemCount <= 8) {
      return {
        jobText: 'text-xs',
        jobTitle: 'text-sm font-bold',
        noteText: 'text-xs',
        containerClass: 'overflow-hidden'
      };
    } else {
      // Many items - use smallest size, allow scroll if needed
      return {
        jobText: 'text-xs',
        jobTitle: 'text-xs font-bold',
        noteText: 'text-xs',
        containerClass: 'overflow-y-auto max-h-[55vh]'
      };
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
      fetchWhiteboard();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchWhiteboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/delivery-whiteboard/${weekStart}`, {
        withCredentials: true
      });
      setWhiteboard(response.data.whiteboard);
    } catch (error) {
      console.error('Failed to fetch whiteboard:', error);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchWhiteboard();
  }, [fetchWhiteboard]);

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const formatDate = (dayIndex) => {
    // Parse weekStart as local date, not UTC
    const [year, month, day] = weekStart.split('-').map(Number);
    const date = new Date(year, month - 1, day + dayIndex);
    return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
  };

  const isToday = (dayIndex) => {
    // Parse weekStart as local date, not UTC
    const [year, month, day] = weekStart.split('-').map(Number);
    const date = new Date(year, month - 1, day + dayIndex);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getWeekLabel = () => {
    const [year, month, day] = weekStart.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    const end = new Date(year, month - 1, day + 4);
    return `${start.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold text-slate-800">Loading Delivery Board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4">
      {/* Header */}
      <header className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">
              DELIVERY WHITEBOARD
            </h1>
            <p className="text-xl text-slate-600">Business Print</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleFullscreen}
              className="p-3 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-6 h-6 text-slate-700" />
              ) : (
                <Maximize2 className="w-6 h-6 text-slate-700" />
              )}
            </button>
            <div className="text-right">
              <p className="text-5xl font-bold text-slate-900 font-mono">
                {currentTime.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-lg text-slate-500">
                {currentTime.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
          </div>
        </div>
        
        {/* Week Navigation */}
        <div className="flex items-center justify-center gap-4 mt-4 bg-white rounded-xl shadow-lg p-3 border-2 border-slate-200">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-slate-700" />
          </button>
          <h2 className="text-2xl font-bold text-slate-800 min-w-[350px] text-center">
            {getWeekLabel()}
          </h2>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-slate-700" />
          </button>
        </div>
      </header>

      {/* Days Grid */}
      <div className="grid grid-cols-5 gap-3">
        {days.map((day, index) => {
          const dayData = whiteboard?.days?.[day] || { noteEntries: [], jobs: [] };
          const notes = dayData.noteEntries || [];
          const jobs = dayData.jobs || [];
          const today = isToday(index);
          const past = isPastDay(index);
          const itemCount = notes.length + jobs.length;
          const styles = getDynamicStyles(itemCount, past);

          return (
            <div 
              key={day}
              className={`
                bg-white rounded-xl shadow-lg border-2 overflow-hidden flex flex-col
                ${today ? 'border-blue-500 ring-4 ring-blue-200' : past ? 'border-slate-300 opacity-75' : 'border-slate-200'}
              `}
            >
              {/* Day Header */}
              <div className={`
                p-3 border-b-2 flex-shrink-0
                ${today ? 'bg-blue-600 border-blue-700' : past ? 'bg-slate-200 border-slate-300' : 'bg-slate-100 border-slate-200'}
              `}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-xl font-black uppercase tracking-wide ${today ? 'text-white' : past ? 'text-slate-500' : 'text-slate-800'}`}>
                      {dayLabels[index]}
                    </h3>
                    <p className={`text-sm font-semibold ${today ? 'text-blue-100' : past ? 'text-slate-400' : 'text-slate-500'}`}>
                      {formatDate(index)}
                    </p>
                  </div>
                  {today && (
                    <span className="px-2 py-1 bg-white text-blue-600 text-xs font-bold rounded-full">
                      TODAY
                    </span>
                  )}
                </div>
              </div>

              {/* Day Content - Dynamic sizing */}
              <div className={`p-2 space-y-2 flex-grow ${styles.containerClass}`}>
                {/* Notes */}
                {notes.map((note, noteIndex) => (
                  <div 
                    key={note.id || noteIndex}
                    className="p-2 bg-amber-50 border border-amber-300 rounded-lg"
                  >
                    <p className={`${styles.noteText} font-semibold text-amber-900 whitespace-pre-wrap line-clamp-2`}>
                      {note.text}
                    </p>
                  </div>
                ))}

                {/* Jobs */}
                {jobs.map((job, jobIndex) => (
                  <div 
                    key={job.job_id || jobIndex}
                    className="p-2 bg-blue-50 border border-blue-300 rounded-lg"
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0 flex-1">
                        <p className={`${styles.jobTitle} text-blue-900 truncate`}>
                          #{job.job_id}
                        </p>
                        <p className={`${styles.jobText} font-medium text-blue-700 truncate`}>
                          {job.customer || 'Customer'}
                        </p>
                        {job.product && (
                          <p className={`${styles.jobText} text-blue-600 truncate`}>
                            {job.product}
                          </p>
                        )}
                      </div>
                      <Truck className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}

                {/* Empty State */}
                {notes.length === 0 && jobs.length === 0 && (
                  <div className="flex items-center justify-center h-20 text-slate-300">
                    <p className="text-base font-medium">No deliveries</p>
                  </div>
                )}
              </div>

              {/* Day Footer - Count */}
              <div className={`
                p-2 border-t-2 text-center flex-shrink-0
                ${today ? 'bg-blue-50 border-blue-200' : past ? 'bg-slate-100 border-slate-200' : 'bg-slate-50 border-slate-200'}
              `}>
                <span className={`text-sm font-bold ${today ? 'text-blue-700' : past ? 'text-slate-400' : 'text-slate-600'}`}>
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="mt-4 text-center">
        <p className="text-slate-400 text-sm">
          Auto-refreshes every 30 seconds • Press F11 or click maximize for fullscreen
        </p>
      </footer>
    </div>
  );
};

// ============================================
// PRODUCTION BOARD PROJECTION (Read-Only View for Screens)
// ============================================

const ProductionBoardProjection = () => {
  const [jobs, setJobs] = useState([]);
  const [activeJobIds, setActiveJobIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  const columns = [
    { id: 'artwork', title: 'ARTWORK', color: 'bg-purple-600', borderColor: 'border-purple-500' },
    { id: 'prepress', title: 'PREPRESS', color: 'bg-blue-600', borderColor: 'border-blue-500' },
    { id: 'digital-print', title: 'DIGITAL', color: 'bg-cyan-600', borderColor: 'border-cyan-500' },
    { id: 'litho-print', title: 'LITHO', color: 'bg-green-600', borderColor: 'border-green-500' },
    { id: 'wide-format', title: 'WIDE FORMAT', color: 'bg-orange-600', borderColor: 'border-orange-500' },
    { id: 'finishing', title: 'FINISHING', color: 'bg-pink-600', borderColor: 'border-pink-500' },
    { id: 'packaging', title: 'PACKAGING', color: 'bg-amber-600', borderColor: 'border-amber-500' },
    { id: 'dispatch', title: 'DISPATCH', color: 'bg-teal-600', borderColor: 'border-teal-500' },
  ];

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/printlogic/synced-jobs?limit=300`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch active job tracking
  const fetchActiveJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/jobs/active-tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activeIds = new Set((response.data.active_jobs || []).map(j => j.job_id));
      setActiveJobIds(activeIds);
    } catch (error) {
      console.error('Failed to fetch active jobs:', error);
    }
  }, []);

  // Auto-refresh every 30 seconds for jobs, every 5 seconds for active tracking
  useEffect(() => {
    fetchJobs();
    fetchActiveJobs();
    const jobsInterval = setInterval(fetchJobs, 30000);
    const activeInterval = setInterval(fetchActiveJobs, 5000);
    return () => {
      clearInterval(jobsInterval);
      clearInterval(activeInterval);
    };
  }, [fetchJobs, fetchActiveJobs]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Group jobs by column
  const getJobsByColumn = (columnId) => {
    return jobs.filter(job => job.board_stage === columnId && job.visible_on_board !== false);
  };

  // Check if job is late
  const isJobLate = (job) => {
    if (!job.date_due || job.date_due === '0000-00-00') return false;
    const dueDate = new Date(job.date_due);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Dynamic sizing based on job count
  const getJobCardStyle = (jobCount) => {
    if (jobCount <= 4) return { padding: 'p-2', text: 'text-sm', title: 'text-base font-bold' };
    if (jobCount <= 8) return { padding: 'p-1.5', text: 'text-xs', title: 'text-sm font-bold' };
    return { padding: 'p-1', text: 'text-xs', title: 'text-xs font-bold' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-cyan-500 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold text-white">Loading Production Board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-zinc-950 flex flex-col overflow-hidden">
      {/* Compact Header */}
      <header className="flex-shrink-0 px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-black text-white tracking-tight">
            PRINTLOGIC <span className="text-cyan-500">PRODUCTION</span>
          </h1>
          <span className="text-zinc-500 text-sm">
            {jobs.length} active jobs
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5 text-zinc-400" />
            ) : (
              <Maximize2 className="w-5 h-5 text-zinc-400" />
            )}
          </button>
          <div className="text-right">
            <p className="text-3xl font-bold text-white font-mono">
              {currentTime.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      </header>

      {/* Columns Grid */}
      <div className="flex-1 grid grid-cols-8 gap-2 p-2 overflow-hidden">
        {columns.map((column) => {
          const columnJobs = getJobsByColumn(column.id);
          const styles = getJobCardStyle(columnJobs.length);
          
          return (
            <div key={column.id} className="flex flex-col min-h-0">
              {/* Column Header */}
              <div className={`${column.color} px-2 py-1.5 rounded-t-lg flex-shrink-0`}>
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-black text-white uppercase tracking-wide truncate">
                    {column.title}
                  </h2>
                  <span className="bg-white/20 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    {columnJobs.length}
                  </span>
                </div>
              </div>

              {/* Jobs List */}
              <div className={`flex-1 bg-zinc-900 border-x-2 border-b-2 ${column.borderColor} rounded-b-lg overflow-y-auto`}>
                <div className="p-1 space-y-1">
                  {columnJobs.map((job) => {
                    const late = isJobLate(job);
                    const isActive = activeJobIds.has(job.job_id);
                    return (
                      <div
                        key={job.job_id}
                        className={`
                          ${styles.padding} rounded border transition-all
                          ${isActive 
                            ? 'bg-green-950 border-green-500 animate-pulse-slow shadow-lg shadow-green-500/30' 
                            : late 
                              ? 'bg-red-950 border-red-700' 
                              : 'bg-zinc-800 border-zinc-700'
                          }
                        `}
                        style={isActive ? { animation: 'pulse-green 2s ease-in-out infinite' } : {}}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="min-w-0 flex-1">
                            <p className={`${styles.title} ${isActive ? 'text-green-400' : late ? 'text-red-400' : 'text-cyan-400'} truncate`}>
                              #{job.job_id}
                            </p>
                            <p className={`${styles.text} ${isActive ? 'text-green-200' : 'text-zinc-300'} truncate`}>
                              {job.customer || 'Customer'}
                            </p>
                            <p className={`${styles.text} ${isActive ? 'text-green-300' : 'text-zinc-500'} truncate`}>
                              {job.product || 'Product'}
                            </p>
                          </div>
                          {isActive && (
                            <Play className="w-3 h-3 text-green-400 flex-shrink-0 animate-pulse" />
                          )}
                          {late && !isActive && (
                            <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {columnJobs.length === 0 && (
                    <div className="text-center py-4 text-zinc-600 text-xs">
                      No jobs
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 px-4 py-1 border-t border-zinc-800 text-center">
        <p className="text-zinc-600 text-xs">
          Auto-refreshes every 30 seconds • Press F11 or click maximize for fullscreen
        </p>
      </footer>
    </div>
  );
};

// ============================================

const formatElapsed = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const JobCard = ({ job, index, onStart, onPause, onComplete, onMove, onDelete, onRetrySync, onViewDetail, onAddToDelivery, columnId, isExpanded, onToggleExpand }) => {
  const isManual = job.isManualTicket;
  const statusClass = `status-${job.status.toLowerCase().replace(' ', '-')}`;
  
  const getSyncStatusBadge = (syncStatus) => {
    const badges = {
      'success': { 
        label: 'Synced', 
        color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', 
        icon: CheckCircle,
        animClass: 'sync-badge-success'
      },
      'failed': { 
        label: 'Sync Failed', 
        color: 'bg-red-500/20 text-red-400 border-red-500/30', 
        icon: AlertTriangle,
        animClass: 'sync-badge-failed'
      },
      'skipped': { 
        label: 'Local Only', 
        color: 'bg-zinc-700 text-zinc-400 border-zinc-600', 
        icon: null,
        animClass: ''
      },
      'pending': { 
        label: 'Syncing', 
        color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', 
        icon: RefreshCw,
        animClass: 'sync-badge-pending'
      },
    };
    return badges[syncStatus] || null;
  };
  
  const getStatusBadgeStyle = (status) => {
    const styles = {
      'Queued': 'bg-zinc-700 text-zinc-300',
      'Ready': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'In Production': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'Paused': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Blocked': 'bg-red-500/20 text-red-400 border-red-500/30',
      'Complete': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Dispatched': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Part Delivered': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    };
    return styles[status] || 'bg-zinc-700 text-zinc-300';
  };

  const getPriorityStyle = (priority) => {
    const styles = {
      'Low': 'text-zinc-500',
      'Normal': 'text-zinc-400',
      'High': 'text-amber-400',
      'Urgent': 'text-red-400 animate-pulse',
    };
    return styles[priority] || 'text-zinc-400';
  };

  const getRouteTypeBadge = (routeType) => {
    const badges = {
      'digital': { label: 'Digital', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
      'litho': { label: 'Litho', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
      'wide-format': { label: 'Wide', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
      'hybrid-digital-wide': { label: 'Digital+Wide', color: 'bg-pink-500/10 text-pink-400 border-pink-500/20' },
      'hybrid-litho-wide': { label: 'Litho+Wide', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
      'hybrid-digital-litho': { label: 'Digital+Litho', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
      'hybrid-wide-digital-litho': { label: 'All Print', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
    };
    return badges[routeType] || null;
  };

  const routeBadge = getRouteTypeBadge(job.routeType);

  // Render Manual Ticket Card (with collapse/expand)
  if (isManual) {
    return (
      <Draggable draggableId={job.id} index={index}>
        {(provided, snapshot) => (
          <article
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            data-testid={`job-card-${job.id}`}
            onClick={(e) => {
              // Don't toggle if clicking buttons
              if (e.target.closest('button')) return;
              onToggleExpand && onToggleExpand(job.id);
            }}
            className={`
              bg-amber-950/30 border border-amber-800/50 border-l-4 border-l-amber-500 rounded-sm
              transition-all duration-300 ease-in-out cursor-pointer
              ${snapshot.isDragging ? 'shadow-2xl scale-[1.02] rotate-1 z-50' : 'hover:border-amber-600/50'}
              ${isExpanded ? 'p-4' : 'p-3'}
            `}
          >
            {/* Collapsed View - Always visible */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <ChevronRight className={`w-4 h-4 text-amber-500/70 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px] font-bold px-1.5 py-0">
                      MANUAL
                    </Badge>
                    <span className="font-mono text-sm font-bold text-amber-400">{job.id}</span>
                  </div>
                  <p className="text-sm text-zinc-400 truncate">{job.contact || job.title}</p>
                </div>
              </div>
              {!isExpanded && (
                <Badge className={`${getStatusBadgeStyle(job.status)} border text-[10px] font-bold uppercase flex-shrink-0`}>
                  {job.status}
                </Badge>
              )}
            </div>

            {/* Expanded View - Animated */}
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
              <div className="space-y-3 pt-2 border-t border-amber-800/30">
                <div className="flex items-center justify-between">
                  <Badge className={`${getStatusBadgeStyle(job.status)} border text-xs font-bold uppercase`}>
                    {job.status}
                  </Badge>
                  {job.priority && job.priority !== 'Normal' && (
                    <span className={`font-bold uppercase text-xs ${getPriorityStyle(job.priority)}`}>
                      {job.priority}
                    </span>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="font-semibold text-zinc-200">{job.title}</p>
                  {job.description && (
                    <p className="text-sm text-zinc-500">{job.description}</p>
                  )}
                  {job.location && (
                    <p className="text-sm text-zinc-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {job.location}
                    </p>
                  )}
                </div>

                <div className="text-sm">
                  <span className="text-zinc-500">
                    Due: <span className="font-medium text-zinc-300">{job.due}</span>
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid={`job-start-${job.id}`}
                    onClick={(e) => { e.stopPropagation(); onStart(job.id); }}
                    className="h-10 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm font-bold uppercase text-xs"
                    disabled={job.status === 'In Production'}
                  >
                    <Play className="w-4 h-4 mr-1" /> Start
                  </Button>
                  <Button
                    data-testid={`job-complete-${job.id}`}
                    onClick={(e) => { e.stopPropagation(); onComplete(job.id); }}
                    className="h-10 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-600/30 rounded-sm font-bold uppercase text-xs"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Done
                  </Button>
                </div>
              </div>
            </div>
          </article>
        )}
      </Draggable>
    );
  }

  // Render Regular PrintLogic Job Card (with collapse/expand)
  return (
    <Draggable draggableId={job.id} index={index}>
      {(provided, snapshot) => (
        <article
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={`job-card-${job.id}`}
          onClick={(e) => {
            // Don't toggle if clicking buttons or the view detail eye icon
            if (e.target.closest('button') || e.target.closest('[data-view-detail]')) return;
            onToggleExpand && onToggleExpand(job.id);
          }}
          className={`
            bg-zinc-900 border border-zinc-800 border-l-4 rounded-sm
            transition-all duration-300 ease-in-out cursor-pointer
            ${statusClass}
            ${snapshot.isDragging ? 'shadow-2xl scale-[1.02] rotate-1 z-50' : 'hover:border-zinc-600'}
            ${job.syncStatus === 'pending' ? 'sync-pending border-amber-500/50' : ''}
            ${isExpanded ? 'p-4' : 'p-3'}
          `}
        >
          {/* Collapsed View - Always visible */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <ChevronRight className={`w-4 h-4 text-zinc-500 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90 text-cyan-400' : ''}`} />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-sm font-bold text-zinc-100">#{job.id}</p>
                <p className="text-sm text-zinc-500 truncate">{job.customer}</p>
              </div>
            </div>
            {!isExpanded && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {routeBadge && (
                  <Badge className={`${routeBadge.color} border text-[10px] font-bold hidden sm:inline-flex`}>
                    {routeBadge.label}
                  </Badge>
                )}
                <Badge className={`${getStatusBadgeStyle(job.status)} border text-[10px] font-bold uppercase`}>
                  {job.status}
                </Badge>
              </div>
            )}
          </div>

          {/* Expanded View - Animated */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[600px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
            <div className="space-y-3 pt-3 border-t border-zinc-800">
              {/* Header with status and view detail */}
              <div className="flex items-center justify-between">
                <Badge className={`${getStatusBadgeStyle(job.status)} border text-xs font-bold uppercase`}>
                  {job.status}
                </Badge>
                {!job.isManualTicket && onViewDetail && (
                  <button
                    data-view-detail="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetail(job.id);
                    }}
                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <Eye className="w-4 h-4" /> View Details
                  </button>
                )}
              </div>

              {/* Job details */}
              <div className="space-y-1">
                <p className="font-semibold text-zinc-200">{job.product}</p>
                <p className="text-sm text-zinc-500">
                  <span className="font-mono font-bold text-zinc-300">{job.qty.toLocaleString()}</span> units
                </p>
                <p className="text-sm text-zinc-400">
                  Due: <span className="font-medium text-zinc-300">{job.due}</span>
                </p>
              </div>

              {/* Route Badge and Sync Status Row */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {routeBadge && (
                  <Badge className={`${routeBadge.color} border text-xs font-bold`}>
                    {routeBadge.label}
                  </Badge>
                )}
                {/* Sync Status Badge */}
                {job.syncStatus && getSyncStatusBadge(job.syncStatus) && (
                  <div className="flex items-center gap-1">
                    <Badge 
                      data-testid={`sync-status-${job.id}`}
                      className={`${getSyncStatusBadge(job.syncStatus).color} ${getSyncStatusBadge(job.syncStatus).animClass} border text-xs font-bold flex items-center gap-1 transition-all duration-300`}
                    >
                      {getSyncStatusBadge(job.syncStatus).icon && (
                        <span className={job.syncStatus === 'pending' ? 'sync-spinner' : ''}>
                          {(() => {
                            const Icon = getSyncStatusBadge(job.syncStatus).icon;
                            return <Icon className="w-3 h-3" />;
                          })()}
                        </span>
                      )}
                      {getSyncStatusBadge(job.syncStatus).label}
                      {job.syncStatus === 'pending' && (
                        <span className="sync-dot-pending w-1.5 h-1.5 rounded-full bg-amber-400 ml-1"></span>
                      )}
                    </Badge>
                    {job.syncStatus === 'failed' && onRetrySync && job.syncLogId && (
                      <Button
                        data-testid={`retry-sync-${job.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRetrySync(job.id, job.syncLogId);
                        }}
                        size="sm"
                        className="h-6 px-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/30 rounded-sm font-bold uppercase text-[10px]"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" /> Retry
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Detail section (if available) */}
              {(job.detail || job.elapsedSeconds > 0 || job.elapsed || job.tag) && (
                <div className="bg-zinc-950 rounded-sm p-3 border border-zinc-800">
                  {job.detail && <p className="text-sm text-zinc-400">{job.detail}</p>}
                  {(job.elapsedSeconds > 0 || job.elapsed) && (
                    <p className="mt-2 font-mono text-lg font-bold text-cyan-400">
                      Elapsed: {formatElapsed(job.elapsedSeconds) || job.elapsed}
                    </p>
                  )}
                  {job.tag && (
                    <p className="mt-2 text-xs font-bold uppercase tracking-widest text-zinc-600">{job.tag}</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid={`job-start-${job.id}`}
                  onClick={(e) => { e.stopPropagation(); onStart(job.id); }}
                  className="h-10 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm font-bold uppercase text-xs"
                  disabled={job.status === 'In Production'}
                >
                  <Play className="w-4 h-4 mr-1" /> Start
                </Button>
                <Button
                  data-testid={`job-pause-${job.id}`}
                  onClick={(e) => { e.stopPropagation(); onPause(job.id); }}
                  variant="outline"
                  className="h-10 border-zinc-700 hover:bg-zinc-800 rounded-sm font-bold uppercase text-xs"
                  disabled={job.status !== 'In Production'}
                >
                  <Pause className="w-4 h-4 mr-1" /> Pause
                </Button>
                <Button
                  data-testid={`job-complete-${job.id}`}
                  onClick={(e) => { e.stopPropagation(); onComplete(job.id); }}
                  className="h-10 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-600/30 rounded-sm font-bold uppercase text-xs"
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Done
                </Button>
                {/* Add to Delivery button - only show in Dispatch column */}
                {columnId === 'dispatch' && onAddToDelivery ? (
                  <Button
                    data-testid={`job-delivery-${job.id}`}
                    onClick={(e) => { e.stopPropagation(); onAddToDelivery(job); }}
                    className="h-10 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-600/30 rounded-sm font-bold uppercase text-xs"
                  >
                    <Calendar className="w-4 h-4 mr-1" /> Delivery
                  </Button>
                ) : (
                  <Button
                    data-testid={`job-move-${job.id}`}
                    onClick={(e) => { e.stopPropagation(); onMove(job.id); }}
                    className="h-10 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/30 rounded-sm font-bold uppercase text-xs"
                  >
                    <ArrowRight className="w-4 h-4 mr-1" /> Move
                  </Button>
                )}
              </div>
            </div>
          </div>
        </article>
      )}
    </Draggable>
  );
};

// Kanban Column Component
const KanbanColumn = ({ column, onStart, onPause, onComplete, onMove, onAddTask, onRetrySync, onViewDetail, onAddToDelivery, onOpenDeliveryBoard, expandedJobId, onToggleExpand }) => {
  const Icon = column.icon;
  const isDispatchColumn = column.id === 'dispatch';
  const isFinishingColumn = column.id === 'finishing';
  const isArtworkColumn = column.id === 'artwork';
  const isPrepressColumn = column.id === 'prepress';
  
  // State for collapsible sub-sections
  const [worksDocketCollapsed, setWorksDocketCollapsed] = useState(false);
  const [waitingArtworkCollapsed, setWaitingArtworkCollapsed] = useState(false);
  const [problemPdfCollapsed, setProblemPdfCollapsed] = useState(false);
  const [finishedBoxedCollapsed, setFinishedBoxedCollapsed] = useState(false);
  const [finishingInProgressCollapsed, setFinishingInProgressCollapsed] = useState(false);
  const [prepressDigitalCollapsed, setPrepressDigitalCollapsed] = useState(false);
  const [prepressLithoCollapsed, setPrepressLithoCollapsed] = useState(false);
  const [prepressWideCollapsed, setPrepressWideCollapsed] = useState(false);
  
  // For Finishing column, split jobs into "In Progress" and "Finished & Boxed"
  const finishingInProgress = isFinishingColumn 
    ? column.jobs.filter(job => job.printlogicStatus !== 'Finished & Boxed')
    : [];
  const finishedAndBoxed = isFinishingColumn 
    ? column.jobs.filter(job => job.printlogicStatus === 'Finished & Boxed')
    : [];
  
  // For Artwork column subcategories
  const artworkWaitingOnArtwork = isArtworkColumn
    ? column.jobs.filter(job => job.printlogicStatus === 'Waiting on Artwork' || job.printlogicStatus === 'Waiting for Artwork')
    : [];
  const artworkProblemPdf = isArtworkColumn
    ? column.jobs.filter(job => job.printlogicStatus === 'Problem with PDF | Job Info')
    : [];
  const worksDocketNotIssued = isArtworkColumn
    ? column.jobs.filter(job => job.printlogicStatus === 'Works Docket Not Issued')
    : [];
  const artworkOther = isArtworkColumn
    ? column.jobs.filter(job => 
        job.printlogicStatus !== 'Waiting on Artwork' && 
        job.printlogicStatus !== 'Waiting for Artwork' &&
        job.printlogicStatus !== 'Problem with PDF | Job Info' &&
        job.printlogicStatus !== 'Works Docket Not Issued'
      )
    : [];
  
  // For Prepress column subcategories
  const prepressDigital = isPrepressColumn
    ? column.jobs.filter(job => job.printlogicStatus === 'Prepress_Digital')
    : [];
  const prepressLitho = isPrepressColumn
    ? column.jobs.filter(job => job.printlogicStatus === 'Prepess_Litho' || job.printlogicStatus === 'Prepress_Litho')
    : [];
  const prepressWideFormat = isPrepressColumn
    ? column.jobs.filter(job => job.printlogicStatus === 'Prepress_Wide Format')
    : [];
  const prepressOther = isPrepressColumn
    ? column.jobs.filter(job => 
        job.printlogicStatus !== 'Prepress_Digital' && 
        job.printlogicStatus !== 'Prepess_Litho' &&
        job.printlogicStatus !== 'Prepress_Litho' &&
        job.printlogicStatus !== 'Prepress_Wide Format'
      )
    : [];
  
  return (
    <div className="w-80 min-w-[320px] flex flex-col h-full bg-zinc-900/30 border-r border-zinc-800" data-testid={`column-${column.id}`}>
      <div className="p-4 border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-zinc-800 rounded-sm">
              <Icon className="w-5 h-5 text-zinc-400" />
            </div>
            <div>
              <h2 className="font-heading font-bold uppercase tracking-wide text-zinc-200">{column.title}</h2>
              <p className="text-xs text-zinc-600 font-mono">{column.jobs.length} items</p>
            </div>
          </div>
          {/* Delivery Whiteboard button - only in Dispatch column */}
          {isDispatchColumn && onOpenDeliveryBoard && (
            <Button
              data-testid="open-delivery-whiteboard"
              onClick={onOpenDeliveryBoard}
              className="bg-purple-600 hover:bg-purple-500 rounded-sm text-xs font-bold uppercase h-9"
            >
              <Clipboard className="w-4 h-4 mr-1" /> Deliveries
            </Button>
          )}
          <div className="flex items-center gap-2">
            <button
              data-testid={`add-task-${column.id}`}
              onClick={() => onAddTask(column)}
              className="p-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-sm transition-colors"
              title="Add manual ticket"
            >
              <Plus className="w-4 h-4" />
            </button>
            <span className="bg-zinc-800 px-3 py-1 rounded-sm font-mono text-sm font-bold text-zinc-300">
              {column.jobs.length}
            </span>
          </div>
        </div>
      </div>

      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-3 overflow-y-auto scrollbar-thin ${snapshot.isDraggingOver ? 'bg-cyan-500/5' : ''}`}
          >
            {/* For Finishing column, show sub-sections */}
            {isFinishingColumn ? (
              <>
                {/* In Progress Section - Collapsible */}
                {finishingInProgress.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => setFinishingInProgressCollapsed(!finishingInProgressCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-amber-500 transition-transform duration-200 ${finishingInProgressCollapsed ? '' : 'rotate-90'}`} />
                      <span className="text-xs font-bold uppercase tracking-wider text-amber-500">In Progress</span>
                      <span className="text-xs font-mono text-amber-500/70">({finishingInProgress.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${finishingInProgressCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-amber-500/5 rounded-sm p-2 mt-2 border border-amber-500/20">
                        {finishingInProgress.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Finished & Boxed Section - Collapsible */}
                {finishedAndBoxed.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setFinishedBoxedCollapsed(!finishedBoxedCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-emerald-500 transition-transform duration-200 ${finishedBoxedCollapsed ? '' : 'rotate-90'}`} />
                      <Package className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-emerald-500">Finished & Boxed</span>
                      <span className="text-xs font-mono text-emerald-500/70">({finishedAndBoxed.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${finishedBoxedCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-emerald-500/5 rounded-sm p-2 mt-2 border border-emerald-500/20">
                        {finishedAndBoxed.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={finishingInProgress.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : isArtworkColumn ? (
              /* Artwork column with collapsible subcategories */
              <>
                {/* Other/Uncategorized Artwork Jobs */}
                {artworkOther.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Other</span>
                      <span className="text-xs font-mono text-zinc-600">({artworkOther.length})</span>
                    </div>
                    <div className="space-y-3">
                      {artworkOther.map((job, index) => (
                        <JobCard 
                          key={job.id} 
                          job={job} 
                          index={index} 
                          onStart={onStart}
                          onPause={onPause}
                          onComplete={onComplete}
                          onMove={onMove}
                          onRetrySync={onRetrySync}
                          onViewDetail={onViewDetail}
                          onAddToDelivery={onAddToDelivery}
                          columnId={column.id}
                          isExpanded={expandedJobId === job.id}
                          onToggleExpand={onToggleExpand}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Waiting on Artwork Section - Collapsible */}
                {artworkWaitingOnArtwork.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setWaitingArtworkCollapsed(!waitingArtworkCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-amber-500 transition-transform duration-200 ${waitingArtworkCollapsed ? '' : 'rotate-90'}`} />
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-amber-500">Waiting on Artwork</span>
                      <span className="text-xs font-mono text-amber-500/70">({artworkWaitingOnArtwork.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${waitingArtworkCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-amber-500/5 rounded-sm p-2 mt-2 border border-amber-500/20">
                        {artworkWaitingOnArtwork.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={artworkOther.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Problem with PDF | Job Info Section - Collapsible */}
                {artworkProblemPdf.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setProblemPdfCollapsed(!problemPdfCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-red-500 transition-transform duration-200 ${problemPdfCollapsed ? '' : 'rotate-90'}`} />
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-red-500">Problem with PDF | Job Info</span>
                      <span className="text-xs font-mono text-red-500/70">({artworkProblemPdf.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${problemPdfCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-red-500/5 rounded-sm p-2 mt-2 border border-red-500/20">
                        {artworkProblemPdf.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={artworkOther.length + artworkWaitingOnArtwork.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Works Docket Not Issued Section - Collapsible */}
                {worksDocketNotIssued.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setWorksDocketCollapsed(!worksDocketCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-orange-500 transition-transform duration-200 ${worksDocketCollapsed ? '' : 'rotate-90'}`} />
                      <FileText className="w-4 h-4 text-orange-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-orange-500">Works Docket Not Issued</span>
                      <span className="text-xs font-mono text-orange-500/70">({worksDocketNotIssued.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${worksDocketCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-orange-500/5 rounded-sm p-2 mt-2 border border-orange-500/20">
                        {worksDocketNotIssued.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={artworkOther.length + artworkWaitingOnArtwork.length + artworkProblemPdf.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : isPrepressColumn ? (
              /* Prepress column with collapsible subcategories */
              <>
                {/* Other/General Prepress Jobs */}
                {prepressOther.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">General</span>
                      <span className="text-xs font-mono text-zinc-600">({prepressOther.length})</span>
                    </div>
                    <div className="space-y-3">
                      {prepressOther.map((job, index) => (
                        <JobCard 
                          key={job.id} 
                          job={job} 
                          index={index} 
                          onStart={onStart}
                          onPause={onPause}
                          onComplete={onComplete}
                          onMove={onMove}
                          onRetrySync={onRetrySync}
                          onViewDetail={onViewDetail}
                          onAddToDelivery={onAddToDelivery}
                          columnId={column.id}
                          isExpanded={expandedJobId === job.id}
                          onToggleExpand={onToggleExpand}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Prepress Digital Section - Collapsible */}
                {prepressDigital.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setPrepressDigitalCollapsed(!prepressDigitalCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-cyan-500 transition-transform duration-200 ${prepressDigitalCollapsed ? '' : 'rotate-90'}`} />
                      <Printer className="w-4 h-4 text-cyan-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-cyan-500">Prepress Digital</span>
                      <span className="text-xs font-mono text-cyan-500/70">({prepressDigital.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${prepressDigitalCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-cyan-500/5 rounded-sm p-2 mt-2 border border-cyan-500/20">
                        {prepressDigital.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={prepressOther.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Prepress Litho Section - Collapsible */}
                {prepressLitho.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setPrepressLithoCollapsed(!prepressLithoCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-indigo-500 transition-transform duration-200 ${prepressLithoCollapsed ? '' : 'rotate-90'}`} />
                      <Printer className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-indigo-500">Prepress Litho</span>
                      <span className="text-xs font-mono text-indigo-500/70">({prepressLitho.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${prepressLithoCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-indigo-500/5 rounded-sm p-2 mt-2 border border-indigo-500/20">
                        {prepressLitho.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={prepressOther.length + prepressDigital.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Prepress Wide Format Section - Collapsible */}
                {prepressWideFormat.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setPrepressWideCollapsed(!prepressWideCollapsed)}
                      className="flex items-center gap-2 w-full px-1 py-2 border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors rounded-sm"
                    >
                      <ChevronRight className={`w-4 h-4 text-teal-500 transition-transform duration-200 ${prepressWideCollapsed ? '' : 'rotate-90'}`} />
                      <Image className="w-4 h-4 text-teal-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-teal-500">Prepress Wide Format</span>
                      <span className="text-xs font-mono text-teal-500/70">({prepressWideFormat.length})</span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${prepressWideCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                      <div className="space-y-3 bg-teal-500/5 rounded-sm p-2 mt-2 border border-teal-500/20">
                        {prepressWideFormat.map((job, index) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            index={prepressOther.length + prepressDigital.length + prepressLitho.length + index} 
                            onStart={onStart}
                            onPause={onPause}
                            onComplete={onComplete}
                            onMove={onMove}
                            onRetrySync={onRetrySync}
                            onViewDetail={onViewDetail}
                            onAddToDelivery={onAddToDelivery}
                            columnId={column.id}
                            isExpanded={expandedJobId === job.id}
                            onToggleExpand={onToggleExpand}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Regular columns - no sub-sections */
              <div className="space-y-3">
                {column.jobs.map((job, index) => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    index={index} 
                    onStart={onStart}
                    onPause={onPause}
                    onComplete={onComplete}
                    onMove={onMove}
                    onRetrySync={onRetrySync}
                    onViewDetail={onViewDetail}
                    onAddToDelivery={onAddToDelivery}
                    columnId={column.id}
                    isExpanded={expandedJobId === job.id}
                    onToggleExpand={onToggleExpand}
                  />
                ))}
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// Main Dashboard
const Dashboard = ({ operator, onLogout }) => {
  // Load PrintLogic config from API
  const { 
    config, 
    loading: configLoading, 
    syncing: configSyncing,
    error: configError, 
    syncInfo,
    refreshConfig,
    syncFromPrintLogic 
  } = usePrintLogicConfig();
  
  const [columns, setColumns] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [activeJobTimer, setActiveJobTimer] = useState(0);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isManualTicketOpen, setIsManualTicketOpen] = useState(false);
  const [isApiTestOpen, setIsApiTestOpen] = useState(false);
  const [isSyncLogOpen, setIsSyncLogOpen] = useState(false);
  const [targetColumn, setTargetColumn] = useState(null);
  const [highlightedJobId, setHighlightedJobId] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [printlogicSyncing, setPrintlogicSyncing] = useState(false);
  const [lastPrintlogicSync, setLastPrintlogicSync] = useState(null);
  const [useLiveData, setUseLiveData] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);  // Default ON
  const [isDeliveryWhiteboardOpen, setIsDeliveryWhiteboardOpen] = useState(false);
  const [isAddToDeliveryOpen, setIsAddToDeliveryOpen] = useState(false);
  const [jobForDelivery, setJobForDelivery] = useState(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [jobToMove, setJobToMove] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [deliveryWeekStart, setDeliveryWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  });
  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const kanbanRef = useRef(null);

  // Toggle job card expansion (only one at a time)
  const handleToggleJobExpand = useCallback((jobId) => {
    setExpandedJobId(prev => prev === jobId ? null : jobId);
  }, []);

  // Fetch active users and send heartbeat
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchActiveUsers = async () => {
      try {
        const response = await axios.get(`${API}/auth/active-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setActiveUsers(response.data.users || []);
      } catch (error) {
        console.error('Failed to fetch active users:', error);
      }
    };

    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/auth/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to send heartbeat:', error);
      }
    };

    // Initial fetch
    fetchActiveUsers();
    sendHeartbeat();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchActiveUsers();
      sendHeartbeat();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Rebuild columns when config is loaded
  useEffect(() => {
    if (config && Object.keys(config.statusMap).length > 0) {
      if (!useLiveData) {
        setColumns(buildBoardColumns(printLogicJobs, config));
      }
    } else if (!configLoading) {
      // Use default config if API fails
      if (!useLiveData) {
        setColumns(buildBoardColumns(printLogicJobs, DEFAULT_CONFIG));
      }
    }
  }, [config, configLoading, useLiveData]);

  // Function to sync orders from PrintLogic
  const syncOrdersFromPrintLogic = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setPrintlogicSyncing(true);
    try {
      // Step 1: Trigger sync to MongoDB
      const syncResponse = await axios.post(
        `${API}/printlogic/sync-orders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (syncResponse.data.success) {
        SoundNotification.click();
        
        // Step 2: Fetch synced jobs from MongoDB
        const jobsResponse = await axios.get(
          `${API}/printlogic/synced-jobs?limit=200`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (jobsResponse.data.success) {
          const liveJobs = jobsResponse.data.jobs;
          
          // Transform synced jobs to board format
          const transformedJobs = liveJobs.map(job => ({
            id: job.job_id,
            title: job.product || `Order #${job.order_number}`,
            customer: job.customer || 'Unknown',
            product: job.product || 'No description',
            qty: job.quantity || 1,
            due: job.date_due && job.date_due !== '0000-00-00' 
              ? new Date(job.date_due).toLocaleDateString() 
              : 'TBD',
            status: job.board_status || 'Ready',
            stage: job.board_stage || 'artwork',
            printlogicStatus: job.printlogic_status,
            orderNumber: job.order_number,
            route: determineRouteFromStatus(job.printlogic_status),
            elapsedSeconds: 0,
            source: 'printlogic',
            isManualTicket: false,
            lastSyncedAt: job.last_synced_at
          }));
          
          // Build columns with live data using the dedicated function
          const currentConfig = config || DEFAULT_CONFIG;
          setColumns(buildColumnsFromLiveData(transformedJobs, currentConfig));
          setUseLiveData(true);
          setLastPrintlogicSync(new Date().toISOString());
          
          SoundNotification.success();
          toast.success(`Loaded ${liveJobs.length} jobs from PrintLogic`, {
            description: `Synced ${syncResponse.data.synced_count} orders`
          });
        }
      }
    } catch (err) {
      console.error('PrintLogic sync failed:', err);
      SoundNotification.error();
      toast.error('Failed to sync from PrintLogic', {
        description: err.response?.data?.detail || err.message
      });
    } finally {
      setPrintlogicSyncing(false);
    }
  };

  // Helper to determine route from PrintLogic status
  const determineRouteFromStatus = (status) => {
    if (!status) return 'standard';
    const s = status.toLowerCase();
    if (s.includes('digital')) return 'digital';
    if (s.includes('litho')) return 'litho';
    if (s.includes('wide')) return 'wide-format';
    if (s.includes('finishing')) return 'standard';
    return 'standard';
  };

  // Stats calculation
  const allJobs = Object.values(columns).flatMap(c => c.jobs);
  const stats = [
    { label: 'In Production', value: allJobs.filter(j => j.status === 'In Production').length, color: 'cyan' },
    { label: 'Waiting', value: allJobs.filter(j => ['Ready', 'Queued'].includes(j.status)).length, color: 'yellow' },
    { label: 'Late Jobs', value: allJobs.filter(j => j.status === 'Blocked' || (j.due?.includes('Today') && !['Complete', 'Dispatched'].includes(j.status))).length, color: 'magenta' },
    { label: 'Completed', value: allJobs.filter(j => ['Complete', 'Dispatched'].includes(j.status)).length, color: 'green' },
  ];

  const bottlenecks = [
    { name: 'Finishing queue', count: columns['finishing']?.jobs.length || 0 },
    { name: 'Artwork issues', count: allJobs.filter(j => j.status === 'Blocked').length },
    { name: 'Wide Format queue', count: columns['wide-format']?.jobs.length || 0 },
  ];

  const estimates = [
    { name: 'Prepress', est: '18m', act: '21m', progress: 55 },
    { name: 'Digital Print', est: '22m', act: '28m', progress: 72 },
    { name: 'Litho Print', est: '3.0h', act: '3.2h', progress: 66 },
    { name: 'Wide Format', est: '45m', act: '52m', progress: 70 },
  ];

  // Clock update
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-refresh from PrintLogic (every 2 minutes when enabled)
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    // If live data not loaded yet, do initial sync
    if (!useLiveData) {
      console.log('Initial sync from PrintLogic on login...');
      syncOrdersFromPrintLogic();
    }
    
    const autoRefreshInterval = setInterval(() => {
      console.log('Auto-refreshing from PrintLogic...');
      syncOrdersFromPrintLogic();
    }, 2 * 60 * 1000); // 2 minutes
    
    return () => clearInterval(autoRefreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshEnabled]);

  // Open order detail modal
  const handleOpenOrderDetail = (jobId) => {
    setSelectedJobId(jobId);
    setIsOrderDetailOpen(true);
  };

  // Active job timer
  useEffect(() => {
    if (activeJob) {
      timerRef.current = setInterval(() => {
        setActiveJobTimer(prev => prev + 1);
        setColumns(prev => {
          const newCols = { ...prev };
          for (const colId in newCols) {
            newCols[colId] = {
              ...newCols[colId],
              jobs: newCols[colId].jobs.map(j => 
                j.id === activeJob.id ? { ...j, elapsedSeconds: (j.elapsedSeconds || 0) + 1 } : j
              )
            };
          }
          return newCols;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeJob]);

  // WebSocket connection
  useEffect(() => {
    const connectWS = () => {
      try {
        wsRef.current = new WebSocket(WS_URL);
        
        wsRef.current.onopen = () => {
          console.log('WebSocket connected');
        };
        
        wsRef.current.onmessage = (event) => {
          const message = JSON.parse(event.data);
          handleWSMessage(message);
        };
        
        wsRef.current.onclose = () => {
          console.log('WebSocket disconnected, reconnecting...');
          setTimeout(connectWS, 3000);
        };
      } catch (error) {
        console.error('WebSocket error:', error);
      }
    };
    
    connectWS();
    
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWSMessage = useCallback((message) => {
    if (message.type === 'job_move') {
      // Handle remote job move
      toast.info(`Job #${message.data.job_id} moved by ${message.data.moved_by}`);
    } else if (message.type === 'status_update') {
      // Handle remote status update
      toast.info(`Job #${message.data.job_id} status updated by ${message.data.updated_by}`);
    }
  }, []);

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const sourceCol = columns[source.droppableId];
    const destCol = columns[destination.droppableId];
    const job = sourceCol.jobs.find(j => j.id === draggableId);

    if (source.droppableId === destination.droppableId) {
      // Reorder within same column
      const newJobs = Array.from(sourceCol.jobs);
      newJobs.splice(source.index, 1);
      newJobs.splice(destination.index, 0, job);
      
      setColumns(prev => ({
        ...prev,
        [source.droppableId]: { ...sourceCol, jobs: newJobs }
      }));
    } else {
      // Move to different column
      const sourceJobs = Array.from(sourceCol.jobs);
      const destJobs = Array.from(destCol.jobs);
      
      sourceJobs.splice(source.index, 1);
      destJobs.splice(destination.index, 0, job);
      
      setColumns(prev => ({
        ...prev,
        [source.droppableId]: { ...sourceCol, jobs: sourceJobs },
        [destination.droppableId]: { ...destCol, jobs: destJobs }
      }));

      toast.success(`Job #${draggableId} moved to ${destCol.title}`);
      
      // Broadcast via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'job_move',
          data: { job_id: draggableId, new_column: destination.droppableId, moved_by: operator.username }
        }));
      }
    }
  };

  // Sync job status to PrintLogic
  const syncStatusToPrintLogic = async (jobId, oldStatus, newStatus) => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // Set pending state immediately for real-time feedback
    setColumns(prev => {
      const newCols = { ...prev };
      for (const colId in newCols) {
        newCols[colId] = {
          ...newCols[colId],
          jobs: newCols[colId].jobs.map(j => 
            j.id === jobId ? { 
              ...j, 
              syncStatus: 'pending',
              syncStartedAt: new Date().toISOString()
            } : j
          )
        };
      }
      return newCols;
    });

    try {
      const response = await axios.post(
        `${API}/jobs/update-status`,
        {
          job_id: jobId,
          new_status: newStatus,
          old_status: oldStatus,
          sync_to_printlogic: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const syncResult = response.data.printlogic_sync;
      
      // Update job with sync status
      setColumns(prev => {
        const newCols = { ...prev };
        for (const colId in newCols) {
          newCols[colId] = {
            ...newCols[colId],
            jobs: newCols[colId].jobs.map(j => 
              j.id === jobId ? { 
                ...j, 
                syncStatus: syncResult.result,
                syncLogId: syncResult.log_id,
                lastSyncAt: new Date().toISOString()
              } : j
            )
          };
        }
        return newCols;
      });

      // Show sync result with sound
      if (syncResult.synced) {
        SoundNotification.success();
        toast.success(`Job #${jobId} synced to PrintLogic`, {
          description: syncResult.message
        });
      } else if (syncResult.result === 'skipped') {
        // Manual ticket - don't show error or play sound
      } else {
        SoundNotification.error();
        toast.warning(`Job #${jobId} - PrintLogic sync failed`, {
          description: syncResult.message
        });
      }

      return syncResult;
    } catch (err) {
      console.error('Sync failed:', err);
      SoundNotification.error();
      
      // Update job with failed sync status
      setColumns(prev => {
        const newCols = { ...prev };
        for (const colId in newCols) {
          newCols[colId] = {
            ...newCols[colId],
            jobs: newCols[colId].jobs.map(j => 
              j.id === jobId ? { 
                ...j, 
                syncStatus: 'failed',
                syncError: err.message
              } : j
            )
          };
        }
        return newCols;
      });

      toast.error(`Job #${jobId} - PrintLogic sync error`, {
        description: err.response?.data?.detail || err.message
      });
      
      return null;
    }
  };

  // Retry failed sync
  const handleRetrySync = async (jobId, syncLogId) => {
    const token = localStorage.getItem('token');
    if (!token || !syncLogId) return;

    try {
      const response = await axios.post(
        `${API}/jobs/retry-sync`,
        { sync_log_id: syncLogId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update job with success status
        setColumns(prev => {
          const newCols = { ...prev };
          for (const colId in newCols) {
            newCols[colId] = {
              ...newCols[colId],
              jobs: newCols[colId].jobs.map(j => 
                j.id === jobId ? { 
                  ...j, 
                  syncStatus: 'success',
                  lastSyncAt: new Date().toISOString()
                } : j
              )
            };
          }
          return newCols;
        });
        SoundNotification.success();
        toast.success(`Job #${jobId} sync retry successful!`);
      } else {
        SoundNotification.error();
        toast.error(`Job #${jobId} sync retry failed`, {
          description: response.data.message
        });
      }
    } catch (err) {
      SoundNotification.error();
      toast.error(`Retry failed: ${err.message}`);
    }
  };

  const handleStart = (jobId) => {
    const job = Object.values(columns).flatMap(c => c.jobs).find(j => j.id === jobId);
    if (job) {
      const oldStatus = job.status;
      setActiveJob(job);
      setActiveJobTimer(job.elapsedSeconds || 0);
      
      // Update local state immediately (optimistic update)
      setColumns(prev => {
        const newCols = { ...prev };
        for (const colId in newCols) {
          newCols[colId] = {
            ...newCols[colId],
            jobs: newCols[colId].jobs.map(j => 
              j.id === jobId ? { ...j, status: 'In Production', syncStatus: 'pending' } : j
            )
          };
        }
        return newCols;
      });
      
      toast.success(`Job #${jobId} started`);
      
      // Track active job for projection displays
      const token = localStorage.getItem('token');
      axios.post(`${API}/jobs/start-tracking`, { job_id: jobId }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => console.error('Failed to track job start:', err));
      
      // Sync to PrintLogic in background
      syncStatusToPrintLogic(jobId, oldStatus, 'In Production');
    }
  };

  const handlePause = (jobId) => {
    const job = Object.values(columns).flatMap(c => c.jobs).find(j => j.id === jobId);
    if (activeJob?.id === jobId) {
      const oldStatus = job?.status || 'In Production';
      setActiveJob(null);
      
      // Update local state immediately
      setColumns(prev => {
        const newCols = { ...prev };
        for (const colId in newCols) {
          newCols[colId] = {
            ...newCols[colId],
            jobs: newCols[colId].jobs.map(j => 
              j.id === jobId ? { ...j, status: 'Paused', syncStatus: 'pending' } : j
            )
          };
        }
        return newCols;
      });
      
      toast.info(`Job #${jobId} paused`);
      
      // Stop tracking for projection displays
      const token = localStorage.getItem('token');
      axios.post(`${API}/jobs/stop-tracking`, { job_id: jobId }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => console.error('Failed to track job stop:', err));
      
      // Sync to PrintLogic in background
      syncStatusToPrintLogic(jobId, oldStatus, 'Paused');
    }
  };

  const handleComplete = async (jobId) => {
    const job = Object.values(columns).flatMap(c => c.jobs).find(j => j.id === jobId);
    const oldStatus = job?.status || 'In Production';
    const elapsedSeconds = job?.elapsedSeconds || 0;
    const currentStage = job?.stage || 'unknown';
    
    if (activeJob?.id === jobId) {
      setActiveJob(null);
      
      // Stop tracking for projection displays
      const token = localStorage.getItem('token');
      axios.post(`${API}/jobs/stop-tracking`, { job_id: jobId }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => console.error('Failed to track job stop:', err));
    }
    
    // Update local state immediately
    setColumns(prev => {
      const newCols = { ...prev };
      for (const colId in newCols) {
        newCols[colId] = {
          ...newCols[colId],
          jobs: newCols[colId].jobs.map(j => 
            j.id === jobId ? { ...j, status: 'Complete', syncStatus: 'pending' } : j
          )
        };
      }
      return newCols;
    });
    
    toast.success(`Job #${jobId} completed!`);
    
    // Sync to PrintLogic in background
    syncStatusToPrintLogic(jobId, oldStatus, 'Complete');
    
    // Log production time to PrintLogic if there was elapsed time
    if (elapsedSeconds > 0 && useLiveData && job?.source === 'printlogic') {
      try {
        await axios.post(
          `${BACKEND_URL}/api/jobs/log-production-time`,
          {
            order_number: jobId,
            stage: currentStage,
            elapsed_seconds: elapsedSeconds,
            notes: `Completed at ${currentStage} stage`
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        toast.success(`Production time logged: ${formatElapsed(elapsedSeconds)}`, { duration: 2000 });
      } catch (error) {
        console.error('Failed to log production time:', error);
        toast.error('Failed to log production time to PrintLogic');
      }
    }
  };

  const handleMove = (jobId) => {
    // Find job and open move modal
    let job = null;
    
    for (const colId in columns) {
      const found = columns[colId].jobs.find(j => j.id === jobId);
      if (found) {
        job = found;
        break;
      }
    }
    
    if (job) {
      setJobToMove(job);
      setIsMoveModalOpen(true);
    }
  };

  // Handle moving job to a specific stage
  const handleMoveToStage = async (jobId, targetStageId, isSubStatus = false) => {
    let job = null;
    let currentColId = null;
    
    for (const colId in columns) {
      const found = columns[colId].jobs.find(j => j.id === jobId);
      if (found) {
        currentColId = colId;
        job = found;
        break;
      }
    }
    
    if (!job) return;

    // Determine the actual column to move to
    let targetColId = targetStageId;
    let newPrintlogicStatus = null;
    
    // Handle "Finished & Boxed" sub-status - moves to finishing column but with special status
    if (targetStageId === 'finished-boxed') {
      targetColId = 'finishing';
      newPrintlogicStatus = 'Finished & Boxed';
    }

    // Map stage IDs to PrintLogic statuses for API sync
    const stageToStatus = {
      'artwork': 'Waiting on Artwork',
      'prepress': 'Prepress',
      'digital-print': 'Digital Print',
      'litho-print': 'Litho Print',
      'wide-format': 'Wide Format',
      'finishing': 'Finishing',
      'packaging': 'Packaging',
      'dispatch': 'Part Delivered'  // Part Delivered goes to dispatch as per user request
    };

    if (!columns[targetColId]) {
      toast.error(`Invalid destination: ${targetStageId}`);
      return;
    }

    // Update the job with new stage and status
    const updatedJob = {
      ...job,
      stage: targetColId,
      printlogicStatus: newPrintlogicStatus || stageToStatus[targetColId] || job.printlogicStatus,
    };

    // Move the job in the UI
    setColumns(prev => ({
      ...prev,
      [currentColId]: {
        ...prev[currentColId],
        jobs: prev[currentColId].jobs.filter(j => j.id !== jobId)
      },
      [targetColId]: {
        ...prev[targetColId],
        jobs: [...prev[targetColId].jobs, updatedJob]
      }
    }));

    // Get display name for toast
    const stageNames = {
      'artwork': 'Artwork',
      'prepress': 'PrePress',
      'digital-print': 'Digital Print',
      'litho-print': 'Litho Print',
      'wide-format': 'Wide Format',
      'finishing': 'Finishing',
      'finished-boxed': 'Finished & Boxed',
      'packaging': 'Packaging',
      'dispatch': 'Dispatch'
    };

    toast.success(`Job #${jobId} moved to ${stageNames[targetStageId] || targetStageId}`);

    // Log production time if job had elapsed time before moving
    const elapsedSeconds = job?.elapsedSeconds || 0;
    if (elapsedSeconds > 0 && useLiveData && job.source === 'printlogic') {
      try {
        await axios.post(
          `${BACKEND_URL}/api/jobs/log-production-time`,
          {
            order_number: jobId,
            stage: currentColId,
            elapsed_seconds: elapsedSeconds,
            notes: `Moved from ${currentColId} to ${stageNames[targetStageId] || targetStageId}`
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        toast.success(`Production time logged: ${formatElapsed(elapsedSeconds)}`, { duration: 2000 });
      } catch (error) {
        console.error('Failed to log production time:', error);
      }
    }

    // If using live data, sync the status change to PrintLogic
    if (useLiveData && job.source === 'printlogic') {
      try {
        const newStatus = newPrintlogicStatus || stageToStatus[targetColId];
        if (newStatus) {
          await axios.post(
            `${BACKEND_URL}/api/jobs/update-status`,
            {
              job_id: job.id,
              old_status: job.printlogicStatus || 'Unknown',
              new_status: newStatus,
              sync_to_printlogic: true
            },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          toast.success(`Status synced to PrintLogic: ${newStatus}`, { duration: 2000 });
        }
      } catch (error) {
        console.error('Failed to sync status to PrintLogic:', error);
        toast.error('Failed to sync status to PrintLogic');
      }
    }
  };

  // Handle adding manual ticket
  const handleOpenAddTask = (column) => {
    setTargetColumn(column);
    setIsManualTicketOpen(true);
  };

  const handleCreateTicket = (ticket, column) => {
    setColumns(prev => ({
      ...prev,
      [column.id]: {
        ...prev[column.id],
        jobs: [...prev[column.id].jobs, {
          ...ticket,
          stage: column.title,
          customer: ticket.contact || 'Internal Task',
          product: ticket.title,
          qty: 1,
        }]
      }
    }));
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Handle job found from barcode scanner
  const handleJobFound = (job) => {
    // Find which column the job is in
    const columnId = job.stage.toLowerCase().replace(/ /g, '-');
    
    // Highlight the job
    setHighlightedJobId(job.id);
    
    // Clear search to show all jobs
    setSearchQuery('');
    
    // Scroll to the column containing the job
    setTimeout(() => {
      const jobElement = document.querySelector(`[data-testid="job-card-${job.id}"]`);
      const columnElement = document.querySelector(`[data-testid="column-${columnId}"]`);
      
      if (kanbanRef.current && columnElement) {
        // Scroll kanban board to show the column
        const columnLeft = columnElement.offsetLeft;
        kanbanRef.current.scrollTo({
          left: columnLeft - 100,
          behavior: 'smooth'
        });
      }
      
      if (jobElement) {
        // Scroll job into view within the column
        jobElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add pulse animation
        jobElement.classList.add('highlight-pulse');
        setTimeout(() => {
          jobElement.classList.remove('highlight-pulse');
          setHighlightedJobId(null);
        }, 3000);
      }
    }, 100);
  };

  // Filter jobs by search query
  const filteredColumns = searchQuery 
    ? Object.fromEntries(
        Object.entries(columns).map(([key, col]) => [
          key,
          {
            ...col,
            jobs: col.jobs.filter(j => 
              j.id.includes(searchQuery) || 
              j.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
              j.product.toLowerCase().includes(searchQuery.toLowerCase())
            )
          }
        ])
      )
    : columns;

  // Show loading state while config is loading
  if (configLoading && Object.keys(columns).length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-cyan-500 animate-spin mx-auto mb-4" />
          <p className="text-lg font-semibold">Loading PrintLogic Configuration...</p>
          <p className="text-sm text-zinc-500 mt-2">Fetching status mappings from API</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col noise-bg" data-testid="dashboard">
      {/* Header */}
      <header className="h-16 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-sm sticky top-0 z-20">
        <div className="h-full max-w-[1920px] mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Factory className="w-7 h-7 text-cyan-500" />
              <div>
                <h1 className="font-heading text-xl font-black tracking-tight uppercase">PrintLogic</h1>
                <p className="text-xs text-zinc-600 uppercase tracking-widest">Shop Floor Board</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-2">
              <span className="font-mono text-lg font-bold text-cyan-400" data-testid="current-time">
                {formatTime(currentTime)}
              </span>
            </div>
            {/* Config Status Indicator */}
            <div 
              data-testid="config-status"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-bold uppercase cursor-pointer hover:opacity-80 ${
                configLoading || configSyncing ? 'bg-amber-500/20 text-amber-400' :
                configError ? 'bg-red-500/20 text-red-400' :
                'bg-emerald-500/20 text-emerald-400'
              }`}
              title={syncInfo.lastSyncedAt 
                ? `Last synced: ${new Date(syncInfo.lastSyncedAt).toLocaleString()} by ${syncInfo.syncedBy || 'system'}\nSource: ${syncInfo.source || 'unknown'}\nTotal statuses: ${syncInfo.totalStatuses}` 
                : 'Not synced yet'}
              onClick={() => {
                if (!configSyncing) {
                  toast.info(`${syncInfo.totalStatuses} statuses loaded from ${syncInfo.source || 'cache'}`);
                }
              }}
            >
              <span className={`w-2 h-2 rounded-full ${
                configLoading || configSyncing ? 'bg-amber-400 animate-pulse' :
                configError ? 'bg-red-400' :
                'bg-emerald-400'
              }`} />
              {configSyncing ? 'Syncing...' : configLoading ? 'Loading' : configError ? 'Offline' : `PrintLogic (${syncInfo.totalStatuses})`}
            </div>
            {/* Sync Orders from PrintLogic Button */}
            <Button
              data-testid="sync-orders-btn"
              className={`${printlogicSyncing ? 'bg-cyan-700' : 'bg-cyan-600 hover:bg-cyan-500'} rounded-sm h-10 font-bold uppercase text-xs`}
              onClick={syncOrdersFromPrintLogic}
              disabled={printlogicSyncing}
            >
              {printlogicSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing Orders...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" /> {useLiveData ? 'Refresh Orders' : 'Load Live Orders'}
                </>
              )}
            </Button>
            {useLiveData && lastPrintlogicSync && (
              <div className="text-xs text-zinc-500 hidden lg:block">
                <span className="text-emerald-400">●</span> Live Data
                <br />
                <span className="text-zinc-600">Last sync: {new Date(lastPrintlogicSync).toLocaleTimeString()}</span>
              </div>
            )}
            {/* Auto-Refresh Toggle */}
            {useLiveData && (
              <Button
                data-testid="auto-refresh-toggle"
                variant="outline"
                className={`rounded-sm h-10 font-bold uppercase text-xs ${
                  autoRefreshEnabled 
                    ? 'border-emerald-600/50 bg-emerald-950/30 text-emerald-400' 
                    : 'border-zinc-700 text-zinc-500 hover:text-zinc-300'
                }`}
                onClick={() => {
                  setAutoRefreshEnabled(!autoRefreshEnabled);
                  if (!autoRefreshEnabled) {
                    toast.success('Auto-refresh enabled', { description: 'Orders will refresh every 2 minutes' });
                  } else {
                    toast.info('Auto-refresh disabled');
                  }
                }}
              >
                <Clock className="w-4 h-4 mr-2" />
                {autoRefreshEnabled ? 'Auto: ON' : 'Auto: OFF'}
              </Button>
            )}
            {operator?.department === 'Admin' && (
              <>
                {/* Sync Log Button (Admin) */}
                <Button
                  data-testid="sync-log-btn"
                  variant="outline"
                  className="border-cyan-800/50 hover:bg-cyan-950/50 text-cyan-400 rounded-sm h-10 font-bold uppercase text-xs"
                  onClick={() => setIsSyncLogOpen(true)}
                >
                  <Eye className="w-4 h-4 mr-2" /> Sync Log
                </Button>
                {/* API Test Button (Admin) */}
                <Button
                  data-testid="api-test-btn"
                  variant="outline"
                  className="border-red-800/50 hover:bg-red-950/50 text-red-400 rounded-sm h-10 font-bold uppercase text-xs"
                  onClick={() => setIsApiTestOpen(true)}
                >
                  <Zap className="w-4 h-4 mr-2" /> API Test
                </Button>
                {/* Admin Panel Button */}
                <Button
                  data-testid="admin-panel-btn"
                  variant="outline"
                  className="border-purple-800/50 hover:bg-purple-950/50 text-purple-400 rounded-sm h-10 font-bold uppercase text-xs"
                  onClick={() => setIsAdminPanelOpen(true)}
                >
                  <Shield className="w-4 h-4 mr-2" /> Admin
                </Button>
              </>
            )}
            <div className="flex items-center gap-3 pl-4 border-l border-zinc-800">
              <div className="text-right">
                <p className="text-sm font-medium text-zinc-300">{operator?.full_name}</p>
                <p className="text-xs text-zinc-600">{operator?.department}</p>
              </div>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                className="text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-sm"
                onClick={onLogout}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Stats Row */}
        <section className="px-4 py-4 border-b border-zinc-800">
          <div className="max-w-[1920px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <Card 
                key={stat.label}
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                className="bg-zinc-900 border-zinc-800 p-4 rounded-sm relative overflow-hidden"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">{stat.label}</p>
                <p className={`mt-2 font-mono text-4xl font-bold ${
                  stat.color === 'cyan' ? 'text-cyan-400' :
                  stat.color === 'yellow' ? 'text-yellow-400' :
                  stat.color === 'magenta' ? 'text-fuchsia-400' :
                  'text-emerald-400'
                }`}>{stat.value}</p>
              </Card>
            ))}
          </div>
          
          {/* Active Users Display */}
          {activeUsers.length > 0 && (
            <div className="max-w-[1920px] mx-auto mt-4">
              <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-sm px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Online Now:</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {activeUsers.map((user, index) => (
                    <span 
                      key={user.username}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-medium ${
                        user.username === operator?.username 
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                          : 'bg-zinc-800 text-zinc-300'
                      }`}
                    >
                      <User className="w-3 h-3" />
                      {user.username}
                      {user.department && (
                        <span className="text-zinc-500 text-[10px]">({user.department})</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Search and Operator Row */}
        <section className="px-4 py-3 border-b border-zinc-800">
          <div className="max-w-[1920px] mx-auto flex flex-col md:flex-row gap-4">
            <div className="flex-1 flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search by job number, customer or product"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-800 rounded-sm h-12 text-base"
                />
              </div>
              <Button className="h-12 bg-zinc-800 hover:bg-zinc-700 rounded-sm font-bold uppercase px-6">
                All Jobs
              </Button>
              <Button variant="outline" className="h-12 border-zinc-700 hover:bg-zinc-800 rounded-sm font-bold uppercase px-6">
                Late Only
              </Button>
              <Button 
                data-testid="open-scanner-btn"
                onClick={() => setIsScannerOpen(true)}
                className="h-12 bg-cyan-600 hover:bg-cyan-500 rounded-sm font-bold uppercase px-6"
              >
                <ScanBarcode className="w-5 h-5 mr-2" /> Scan
              </Button>
            </div>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm px-4 py-2 flex items-center justify-between min-w-[280px]">
              <div>
                <p className="text-xs text-zinc-600 uppercase tracking-widest">Current Operator</p>
                <p className="font-semibold text-zinc-200">{operator?.full_name}</p>
              </div>
              <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-bold uppercase">
                {operator?.department}
              </Badge>
            </Card>
          </div>
        </section>

        {/* Kanban + Sidebar */}
        <section className="flex-1 flex overflow-hidden">
          {/* Kanban Board - Scrollable both directions */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <div ref={kanbanRef} className="flex-1 overflow-auto" data-testid="kanban-board">
              <div className="flex h-full min-w-max min-h-full">
                {Object.values(filteredColumns).map(column => (
                  <KanbanColumn 
                    key={column.id} 
                    column={column}
                    onStart={handleStart}
                    onPause={handlePause}
                    onComplete={handleComplete}
                    onMove={handleMove}
                    onAddTask={handleOpenAddTask}
                    onRetrySync={handleRetrySync}
                    onViewDetail={handleOpenOrderDetail}
                    onAddToDelivery={(job) => {
                      setJobForDelivery(job);
                      setIsAddToDeliveryOpen(true);
                    }}
                    onOpenDeliveryBoard={() => setIsDeliveryWhiteboardOpen(true)}
                    expandedJobId={expandedJobId}
                    onToggleExpand={handleToggleJobExpand}
                  />
                ))}
              </div>
            </div>
          </DragDropContext>

          {/* Sidebar */}
          <aside className="w-80 border-l border-zinc-800 bg-zinc-950/50 overflow-y-auto scrollbar-thin" data-testid="sidebar">
            {/* Active Run Timer */}
            <div className="p-4 border-b border-zinc-800">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Active Run</p>
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-sm p-5 relative scanline">
                <p className="font-mono text-5xl font-bold text-cyan-400 tracking-tight" data-testid="active-timer">
                  {activeJob ? formatTimer(activeJobTimer) : '--:--'}
                </p>
                {activeJob ? (
                  <>
                    <p className="mt-4 font-bold text-lg text-zinc-200">Job #{activeJob.id}</p>
                    <p className="text-zinc-400">{activeJob.product} · {activeJob.qty.toLocaleString()} units</p>
                    <p className="text-sm text-zinc-600 mt-1">{activeJob.customer}</p>
                  </>
                ) : (
                  <p className="mt-4 text-zinc-600">No active job</p>
                )}
              </div>
            </div>

            {/* Bottlenecks */}
            <div className="p-4 border-b border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Bottlenecks</p>
                <Badge className="bg-fuchsia-500/20 text-fuchsia-400 border border-fuchsia-500/30 font-bold uppercase text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Attention
                </Badge>
              </div>
              <div className="space-y-2">
                {bottlenecks.map(item => (
                  <div key={item.name} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3">
                    <span className="text-sm text-zinc-400">{item.name}</span>
                    <span className="font-mono font-bold text-zinc-200">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Estimate vs Actual */}
            <div className="p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Estimate vs Actual</p>
              <div className="space-y-3">
                {estimates.map(item => (
                  <div key={item.name} className="bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-zinc-400">{item.name}</span>
                      <span className="font-mono text-zinc-300">{item.est} / {item.act}</span>
                    </div>
                    <Progress value={item.progress} className="h-2 bg-zinc-800" />
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </section>
      </main>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal 
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onJobFound={handleJobFound}
        allJobs={allJobs}
      />

      {/* Manual Ticket Modal */}
      <ManualTicketModal
        isOpen={isManualTicketOpen}
        onClose={() => {
          setIsManualTicketOpen(false);
          setTargetColumn(null);
        }}
        onCreateTicket={handleCreateTicket}
        targetColumn={targetColumn}
      />

      {/* PrintLogic API Test Modal (Admin Only) */}
      <PrintLogicTestModal
        isOpen={isApiTestOpen}
        onClose={() => setIsApiTestOpen(false)}
      />

      {/* PrintLogic Sync Log Modal (Admin) */}
      <SyncLogModal
        isOpen={isSyncLogOpen}
        onClose={() => setIsSyncLogOpen(false)}
      />

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={isOrderDetailOpen}
        onClose={() => setIsOrderDetailOpen(false)}
        jobId={selectedJobId}
      />

      {/* Delivery Whiteboard */}
      <DeliveryWhiteboard
        isOpen={isDeliveryWhiteboardOpen}
        onClose={() => setIsDeliveryWhiteboardOpen(false)}
        onViewJobDetail={(jobId) => {
          setSelectedJobId(jobId);
          setIsOrderDetailOpen(true);
        }}
      />

      {/* Add to Delivery Modal */}
      <AddToDeliveryModal
        isOpen={isAddToDeliveryOpen}
        onClose={() => {
          setIsAddToDeliveryOpen(false);
          setJobForDelivery(null);
        }}
        job={jobForDelivery}
        weekStart={deliveryWeekStart}
      />

      {/* Move Stage Modal */}
      <MoveStageModal
        isOpen={isMoveModalOpen}
        onClose={() => {
          setIsMoveModalOpen(false);
          setJobToMove(null);
        }}
        job={jobToMove}
        onMoveToStage={handleMoveToStage}
      />

      {/* Admin Panel Modal */}
      <AdminPanelModal
        isOpen={isAdminPanelOpen}
        onClose={() => setIsAdminPanelOpen(false)}
      />

      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
};

// Main App
const safeNextPath = () => {
  const next = new URLSearchParams(window.location.search).get("next") || "/";
  return next.startsWith("/") && !next.startsWith("//") ? next : "/";
};

function App() {
  const { token, operator, login, logout, isAuthenticated } = useAuth();

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? <Navigate to={safeNextPath()} replace /> : <LoginPage onLogin={login} />
            } 
          />
          <Route 
            path="/" 
            element={
              isAuthenticated ? <Dashboard operator={operator} onLogout={logout} /> : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/delivery-projection" 
            element={
              isAuthenticated ? <ProjectionDeliveryBoard /> : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/production-projection" 
            element={
              isAuthenticated ? <ProductionBoardProjection /> : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/work-terminal" 
            element={<OperatorWorkTerminal />}
          />
          <Route 
            path="/kiosk/production" 
            element={<KioskProductionBoard />}
          />
          <Route 
            path="/kiosk/delivery" 
            element={<KioskDeliveryBoard />}
          />
          <Route 
            path="/kiosk/wide-format-dept" 
            element={<KioskWideFormatDept />}
          />
          <Route 
            path="/kiosk/prepress-dept" 
            element={<KioskPrepressDept />}
          />
          <Route 
            path="/request-delivery" 
            element={<DeliveryRequestForm />}
          />
          <Route 
            path="/sop-toolkit" 
            element={<SOPToolkit />}
          />
          <Route 
            path="/hub" 
            element={<ToolsHub />}
          />
          <Route 
            path="/dispatch" 
            element={<DispatchBoard />}
          />
          <Route 
            path="/box-labels" 
            element={<BoxLabelsTool />}
          />
          <Route 
            path="/wide-format" 
            element={
              isAuthenticated 
                ? <WideFormatDashboard onBack={() => window.history.back()} /> 
                : <Navigate to="/login" replace />
            }
          />
          <Route 
            path="/sales" 
            element={isAuthenticated ? <SalesDashboard onSessionExpired={logout} /> : <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
